import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { LogOut } from 'lucide-react';
import { collection, onSnapshot, doc, setDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';

const SOL_MINT = 'So11111111111111111111111111111111111111112';

function shortenAddr(addr) { if (!addr) return ''; return addr.slice(0, 4) + '...' + addr.slice(-4); }
function tokenLabel(trade) { if (!trade) return 'TOKEN'; if (trade.mint === SOL_MINT) return 'SOL'; return trade.token || 'TOKEN'; }
function formatUSD(n) {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e9) return '$' + (n / 1e9).toFixed(2) + 'B';
  if (abs >= 1e6) return '$' + (n / 1e6).toFixed(2) + 'M';
  if (abs >= 1e3) return '$' + (n / 1e3).toFixed(1) + 'K';
  return '$' + n.toFixed(2);
}
function formatPrice(p) {
  if (p === null || p === undefined || isNaN(p)) return '—';
  if (p < 0.00001) return '$' + p.toExponential(2);
  if (p < 0.01) return '$' + p.toFixed(8);
  if (p < 1) return '$' + p.toFixed(5);
  return '$' + p.toFixed(4);
}
function formatAge(ms) {
  if (!ms) return '—';
  const hrs = (Date.now() - ms) / 3600000;
  if (hrs < 1) return Math.round(hrs * 60) + 'm';
  if (hrs < 48) return Math.round(hrs) + 'h';
  return Math.round(hrs / 24) + 'd';
}
function timeSince(ms) {
  if (!ms) return '';
  const mins = (Date.now() - ms) / 60000;
  if (mins < 1) return 'just now';
  if (mins < 60) return Math.round(mins) + 'm ago';
  if (mins < 1440) return Math.round(mins / 60) + 'h ago';
  return Math.round(mins / 1440) + 'd ago';
}
function holdDuration(openMs, closeMs) {
  if (!openMs || !closeMs) return '—';
  const mins = (closeMs - openMs) / 60000;
  if (mins < 60) return Math.round(mins) + 'm';
  if (mins < 1440) return (mins / 60).toFixed(1) + 'h';
  return (mins / 1440).toFixed(1) + 'd';
}
function typeBadgeClass(type) {
  if (type === 'BUY') return 'bg-green-950 text-green-400';
  if (type === 'SELL') return 'bg-red-950 text-red-400';
  return 'bg-blue-950 text-blue-400';
}
function todayKey() { const d = new Date(); return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`; }
function multiplierStr(pnlPct) {
  const mult = 1 + (pnlPct / 100);
  if (mult >= 2) return mult.toFixed(1) + 'x';
  if (mult <= 0) return '0x';
  return null;
}

function MT({ onLogout }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [mode, setMode] = useState('demo');
  const [solPrice, setSolPrice] = useState(0);
  const [solChange, setSolChange] = useState(0);
  const [wallets, setWallets] = useState([]);
  const [trades, setTrades] = useState([]);

  // ACCOUNT - Firestore synced, cross-device
  const [reserveBalance, setReserveBalance] = useState(100000);
  const [tradingBalance, setTradingBalance] = useState(0);

  // POSITIONS + HISTORY - generic, works for Demo AND Live
  const [positions, setPositions] = useState([]);
  const [history, setHistory] = useState([]);
  const [wins, setWins] = useState(0);

  // AUTO PILOT SETTINGS
  const [autoPilot, setAutoPilot] = useState(false);
  const [globalAmt, setGlobalAmt] = useState('50');
  const [globalTP, setGlobalTP] = useState('');
  const [globalSL, setGlobalSL] = useState('');
  const [globalTrail, setGlobalTrail] = useState('');
  const [globalTimeLimit, setGlobalTimeLimit] = useState('');
  const [newTokensOnly, setNewTokensOnly] = useState(false);
  const [copySell, setCopySell] = useState(false);
  const [autoMinMC, setAutoMinMC] = useState('');
  const [autoMaxMC, setAutoMaxMC] = useState('');

  // SAFETY LIMITS
  const [dailyLossLimit, setDailyLossLimit] = useState('');
  const [maxDailyTrades, setMaxDailyTrades] = useState('');
  const [maxOpenPositions, setMaxOpenPositions] = useState('');
  const [walletCooldown, setWalletCooldown] = useState('2');

  const [skipLog, setSkipLog] = useState([]);

  // NOTIFICATIONS
  const [notifNewTrade, setNotifNewTrade] = useState(true);
  const [notifAutoCopy, setNotifAutoCopy] = useState(true);
  const [notifPnl, setNotifPnl] = useState(true);
  const [notifSound, setNotifSound] = useState(false);

  // BLACKLIST + PINNED + IGNORED
  const [ignoredTrades, setIgnoredTrades] = useState([]);
  const [blWallets, setBlWallets] = useState([]);
  const [blMints, setBlMints] = useState([]);
  const [pinnedWallets, setPinnedWallets] = useState([]);
  const [blWalletInput, setBlWalletInput] = useState('');
  const [blMintInput, setBlMintInput] = useState('');

  // PRICE ALERTS (upgrade #24)
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [alertMint, setAlertMint] = useState('');
  const [alertPrice, setAlertPrice] = useState('');
  const [alertMC, setAlertMC] = useState('');
  const [alertLabel, setAlertLabel] = useState('');
  const [firedAlerts, setFiredAlerts] = useState([]);

  // DAILY STATS
  const [dailyStats, setDailyStats] = useState({});

  // DEX CACHE + POSITION PRICES
  const [dexCache, setDexCache] = useState({});
  const [positionPrices, setPositionPrices] = useState({});
  const [positionMCs, setPositionMCs] = useState({});
  const fetchingMints = useRef(new Set());

  // BLIPS
  const [blips, setBlips] = useState({});
  const prevValues = useRef({});
  const prevSolPrice = useRef(0);
  const prevWorth = useRef(0);

  // UI STATE
  const [feedFilter, setFeedFilter] = useState('all');
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [expandedTrade, setExpandedTrade] = useState(null);
  const [statusPanelTrade, setStatusPanelTrade] = useState(null);

  // MODALS
  const [showAddWallet, setShowAddWallet] = useState(false);
  const [newWalletName, setNewWalletName] = useState('');
  const [newWalletAddr, setNewWalletAddr] = useState('');
  const [newWalletMode, setNewWalletMode] = useState('watch');
  const [newWalletColor, setNewWalletColor] = useState('#3fb950');
  const [newWalletAmt, setNewWalletAmt] = useState('');
  const [newWalletMaxExp, setNewWalletMaxExp] = useState('');
  const [showEditWallet, setShowEditWallet] = useState(false);
  const [editWallet, setEditWallet] = useState(null);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyTrade, setCopyTrade] = useState(null);
  const [copyAmt, setCopyAmt] = useState('50');
  const [showDelModal, setShowDelModal] = useState(false);
  const [delWallet, setDelWallet] = useState(null);
  const [showRealNotice, setShowRealNotice] = useState(false);
  const [showTokPop, setShowTokPop] = useState(false);
  const [tokPopMint, setTokPopMint] = useState(null);
  const [tokPopLabel, setTokPopLabel] = useState('');
  const [showSellModal, setShowSellModal] = useState(false);
  const [sellPosIdx, setSellPosIdx] = useState(null);
  const [sellPct, setSellPct] = useState(100);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [rulesPosIdx, setRulesPosIdx] = useState(null);
  const [rulesSL, setRulesSL] = useState('');
  const [rulesTP, setRulesTP] = useState('');
  const [rulesTrail, setRulesTrail] = useState('');
  const [rulesDNS, setRulesDNS] = useState(false);
  const [rulesParts, setRulesParts] = useState([]);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferAmt, setTransferAmt] = useState('');
  const [transferDir, setTransferDir] = useState('toTrading');
  const [copyFeedback, setCopyFeedback] = useState(null);
  const [newTradeAlert, setNewTradeAlert] = useState(false);
  const [lastTradeCount, setLastTradeCount] = useState(0);
  const [toast, setToast] = useState(null);

  const isDemo = mode === 'demo';
  const uid = auth.currentUser?.uid;
  const saveTimer = useRef(null);

  // ============ FIRESTORE SAVE (debounced, 1s) ============
  const saveToFirestore = useCallback((patch) => {
    if (!uid) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      setDoc(doc(db, 'users', uid, 'mtData', 'state'), patch, { merge: true }).catch(() => {});
    }, 1000);
  }, [uid]);

  // ============ FIRESTORE LOAD (onSnapshot - cross device) ============
  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(doc(db, 'users', uid, 'mtData', 'state'), (snap) => {
      if (!snap.exists()) return;
      const d = snap.data();
      if (d.reserveBalance !== undefined) setReserveBalance(d.reserveBalance);
      if (d.tradingBalance !== undefined) setTradingBalance(d.tradingBalance);
      if (d.positions !== undefined) setPositions(d.positions);
      if (d.history !== undefined) setHistory(d.history);
      if (d.wins !== undefined) setWins(d.wins);
      if (d.dailyStats !== undefined) setDailyStats(d.dailyStats);
      if (d.skipLog !== undefined) setSkipLog(d.skipLog);
      if (d.ignoredTrades !== undefined) setIgnoredTrades(d.ignoredTrades);
      if (d.blWallets !== undefined) setBlWallets(d.blWallets);
      if (d.blMints !== undefined) setBlMints(d.blMints);
      if (d.pinnedWallets !== undefined) setPinnedWallets(d.pinnedWallets);
      if (d.priceAlerts !== undefined) setPriceAlerts(d.priceAlerts);
      if (d.autoPilot !== undefined) setAutoPilot(d.autoPilot);
      if (d.globalAmt !== undefined) setGlobalAmt(d.globalAmt);
      if (d.globalTP !== undefined) setGlobalTP(d.globalTP);
      if (d.globalSL !== undefined) setGlobalSL(d.globalSL);
      if (d.globalTrail !== undefined) setGlobalTrail(d.globalTrail);
      if (d.globalTimeLimit !== undefined) setGlobalTimeLimit(d.globalTimeLimit);
      if (d.newTokensOnly !== undefined) setNewTokensOnly(d.newTokensOnly);
      if (d.copySell !== undefined) setCopySell(d.copySell);
      if (d.autoMinMC !== undefined) setAutoMinMC(d.autoMinMC);
      if (d.autoMaxMC !== undefined) setAutoMaxMC(d.autoMaxMC);
      if (d.dailyLossLimit !== undefined) setDailyLossLimit(d.dailyLossLimit);
      if (d.maxDailyTrades !== undefined) setMaxDailyTrades(d.maxDailyTrades);
      if (d.maxOpenPositions !== undefined) setMaxOpenPositions(d.maxOpenPositions);
      if (d.walletCooldown !== undefined) setWalletCooldown(d.walletCooldown);
      if (d.notifNewTrade !== undefined) setNotifNewTrade(d.notifNewTrade);
      if (d.notifAutoCopy !== undefined) setNotifAutoCopy(d.notifAutoCopy);
      if (d.notifPnl !== undefined) setNotifPnl(d.notifPnl);
      if (d.notifSound !== undefined) setNotifSound(d.notifSound);
      if (d.mode !== undefined) setMode(d.mode);
    });
    return () => unsub();
  }, [uid]);


  // ============ SOL PRICE ============
  useEffect(() => {
    const fetchSol = async () => {
      try {
        const res = await fetch('https://api.binance.com/api/v3/ticker/24hr?symbol=SOLUSDT');
        const data = await res.json();
        const price = parseFloat(data.lastPrice);
        setSolPrice(price); setSolChange(parseFloat(data.priceChangePercent));
        if (prevSolPrice.current && price !== prevSolPrice.current) triggerBlip('sol', price > prevSolPrice.current ? 'up' : 'down');
        prevSolPrice.current = price;
      } catch (e) {}
    };
    fetchSol();
    const iv = setInterval(fetchSol, 5000);
    return () => clearInterval(iv);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============ WALLETS ============
  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(collection(db, 'users', uid, 'mirror_wallets'), (snap) => {
      setWallets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [uid]);

  // ============ TRADES ============
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'mirror_trades'), (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => {
        const ta = a.timestamp?.toMillis ? a.timestamp.toMillis() : 0;
        const tb = b.timestamp?.toMillis ? b.timestamp.toMillis() : 0;
        return tb - ta;
      });
      if (lastTradeCount > 0 && data.length > lastTradeCount && notifNewTrade) {
        setNewTradeAlert(true);
        setTimeout(() => setNewTradeAlert(false), 3000);
        if (notifSound) {
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator(); osc.frequency.value = 880;
            osc.connect(ctx.destination); osc.start(); osc.stop(ctx.currentTime + 0.1);
          } catch (e) {}
        }
      }
      setLastTradeCount(data.length);
      setTrades(data.slice(0, 60));
    });
    return () => unsub();
  }, [lastTradeCount, notifNewTrade, notifSound]);

  const triggerBlip = useCallback((key, dir) => {
    setBlips(prev => ({ ...prev, [key]: dir }));
    setTimeout(() => setBlips(prev => { const n = { ...prev }; delete n[key]; return n; }), 600);
  }, []);

  const showToast = useCallback((message, kind) => {
    setToast({ message, kind: kind || 'info', id: Date.now() });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchDex = useCallback(async (mint) => {
    if (!mint || mint === SOL_MINT) return null;
    try {
      const res = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${mint}`);
      const data = await res.json();
      const pair = data.pairs && data.pairs.length > 0 ? data.pairs[0] : null;
      if (pair) return {
        priceUsd: pair.priceUsd ? parseFloat(pair.priceUsd) : null, symbol: pair.baseToken?.symbol || null,
        liquidityUsd: pair.liquidity?.usd || null, fdv: pair.fdv || null, volume24h: pair.volume?.h24 || null,
        priceChange24h: pair.priceChange?.h24 || null, ageMs: pair.pairCreatedAt || null,
        icon: pair.info?.imageUrl || null, url: pair.url || `https://dexscreener.com/solana/${mint}`, found: true
      };
    } catch (e) {}
    return { found: false, url: `https://dexscreener.com/solana/${mint}` };
  }, []);

  const ensureDexData = useCallback(async (mint) => {
    if (!mint || mint === SOL_MINT || dexCache[mint] || fetchingMints.current.has(mint)) return;
    fetchingMints.current.add(mint);
    const info = await fetchDex(mint);
    if (info) setDexCache(prev => ({ ...prev, [mint]: info }));
    fetchingMints.current.delete(mint);
  }, [dexCache, fetchDex]);

  const fetchPrice = useCallback(async (mint) => {
    if (mint === SOL_MINT) return null;
    const dex = await fetchDex(mint);
    if (dex?.found && dex.priceUsd) return { price: dex.priceUsd, mc: dex.fdv };
    try {
      const res = await fetch(`https://price.jup.ag/v6/price?ids=${mint}`);
      const data = await res.json();
      if (data.data?.[mint]?.price) return { price: data.data[mint].price, mc: null };
    } catch (e) {}
    return null;
  }, [fetchDex]);

  useEffect(() => {
    const mints = [...new Set(trades.map(t => t.mint).filter(m => m && m !== SOL_MINT && !dexCache[m]))];
    mints.slice(0, 12).forEach((mint, i) => setTimeout(() => ensureDexData(mint), i * 250));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trades]);

  const fetchPositionData = useCallback(async (mints) => {
    if (!mints.length) return;
    const results = await Promise.all(mints.map(async m => [m, await fetchPrice(m)]));
    const prices = {}; const mcs = {};
    results.forEach(([m, info]) => { if (info) { prices[m] = info.price; if (info.mc) mcs[m] = info.mc; } });
    if (Object.keys(prices).length) setPositionPrices(prev => ({ ...prev, ...prices }));
    if (Object.keys(mcs).length) setPositionMCs(prev => ({ ...prev, ...mcs }));
  }, [fetchPrice]);

  // 1-second polling (upgrade #11)
  useEffect(() => {
    const mints = [...new Set(positions.filter(p => p.open && p.mint).map(p => p.mint))];
    if (!mints.length) return;
    fetchPositionData(mints);
    const iv = setInterval(() => fetchPositionData(mints), 1000);
    return () => clearInterval(iv);
  }, [positions, fetchPositionData]);

  // price alerts check (upgrade #24)
  useEffect(() => {
    if (!priceAlerts.length) return;
    priceAlerts.forEach(alert => {
      if (firedAlerts.includes(alert.id)) return;
      const dex = dexCache[alert.mint]; if (!dex?.found) return;
      const priceHit = alert.targetPrice && dex.priceUsd && dex.priceUsd >= parseFloat(alert.targetPrice);
      const mcHit = alert.targetMC && dex.fdv && dex.fdv >= parseFloat(alert.targetMC);
      if (priceHit || mcHit) {
        setFiredAlerts(prev => [...prev, alert.id]);
        showToast(`🎯 ${alert.label || shortenAddr(alert.mint)} hit ${priceHit ? formatPrice(dex.priceUsd) : formatUSD(dex.fdv)}`, 'success');
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dexCache, priceAlerts]);

  const doTransfer = () => {
    const amt = parseFloat(transferAmt) || 0; if (amt <= 0) return;
    if (transferDir === 'toTrading') {
      if (amt > reserveBalance) return alert('Not enough in reserve!');
      const nr = Math.round((reserveBalance - amt) * 100) / 100; const nt = Math.round((tradingBalance + amt) * 100) / 100;
      setReserveBalance(nr); setTradingBalance(nt); saveToFirestore({ reserveBalance: nr, tradingBalance: nt });
      showToast(`Transferred $${amt} to Trading Wallet`, 'success');
    } else {
      if (amt > tradingBalance) return alert('Not enough free in trading wallet!');
      const nr = Math.round((reserveBalance + amt) * 100) / 100; const nt = Math.round((tradingBalance - amt) * 100) / 100;
      setReserveBalance(nr); setTradingBalance(nt); saveToFirestore({ reserveBalance: nr, tradingBalance: nt });
      showToast(`Transferred $${amt} to Reserve`, 'success');
    }
    setTransferAmt(''); setShowTransferModal(false);
  };

  const addWallet = async () => {
    if (!newWalletAddr || !newWalletName || !uid) return;
    try {
      await setDoc(doc(db, 'users', uid, 'mirror_wallets', newWalletAddr), {
        name: newWalletName, address: newWalletAddr, mode: newWalletMode, color: newWalletColor,
        amtPerTrade: newWalletAmt || null, maxExposure: newWalletMaxExp || null, addedAt: new Date().toISOString()
      });
      await setDoc(doc(db, 'mirror_watchers', newWalletAddr), { wallet: newWalletAddr, status: 'pending', name: newWalletName, mode: newWalletMode });
      setNewWalletName(''); setNewWalletAddr(''); setNewWalletAmt(''); setNewWalletMaxExp(''); setNewWalletMode('watch'); setShowAddWallet(false);
    } catch (e) { alert('Error: ' + e.message); }
  };

  const saveEditWallet = async () => {
    if (!editWallet || !uid) return;
    try {
      await updateDoc(doc(db, 'users', uid, 'mirror_wallets', editWallet.address), {
        name: editWallet.name, mode: editWallet.mode, color: editWallet.color,
        amtPerTrade: editWallet.amtPerTrade || null, maxExposure: editWallet.maxExposure || null
      });
      await updateDoc(doc(db, 'mirror_watchers', editWallet.address), { mode: editWallet.mode }).catch(() => {});
      setShowEditWallet(false); setEditWallet(null);
    } catch (e) { alert('Error: ' + e.message); }
  };

  const cycleWalletMode = async (w) => {
    if (!uid) return;
    const order = ['watch', 'copy', 'shadow'];
    const nextMode = order[(order.indexOf(w.mode) + 1) % order.length];
    try {
      await updateDoc(doc(db, 'users', uid, 'mirror_wallets', w.address), { mode: nextMode });
      await updateDoc(doc(db, 'mirror_watchers', w.address), { mode: nextMode }).catch(() => {});
    } catch (e) {}
  };

  const removeWallet = async () => {
    if (!delWallet || !uid) return;
    try {
      await deleteDoc(doc(db, 'users', uid, 'mirror_wallets', delWallet.address));
      await deleteDoc(doc(db, 'mirror_watchers', delWallet.address));
    } catch (e) {}
    setShowDelModal(false); setDelWallet(null);
    if (selectedWallet === delWallet.address) setSelectedWallet(null);
  };

  const pauseAll = async () => {
    if (!uid) return;
    for (const w of wallets) {
      if (w.mode === 'copy' || w.mode === 'shadow') {
        try {
          await updateDoc(doc(db, 'users', uid, 'mirror_wallets', w.address), { mode: 'watch' });
          await updateDoc(doc(db, 'mirror_watchers', w.address), { mode: 'watch' }).catch(() => {});
        } catch (e) {}
      }
    }
    showToast('All wallets set to WATCH', 'info');
  };

  const togglePin = (addr) => {
    const updated = pinnedWallets.includes(addr) ? pinnedWallets.filter(a => a !== addr) : [...pinnedWallets, addr];
    setPinnedWallets(updated); saveToFirestore({ pinnedWallets: updated });
  };

  const addBlacklistWallet = () => {
    if (!blWalletInput.trim()) return;
    const u = [...new Set([...blWallets, blWalletInput.trim()])]; setBlWallets(u); setBlWalletInput(''); saveToFirestore({ blWallets: u });
  };
  const removeBlacklistWallet = (addr) => { const u = blWallets.filter(a => a !== addr); setBlWallets(u); saveToFirestore({ blWallets: u }); };
  const addBlacklistMint = () => {
    if (!blMintInput.trim()) return;
    const u = [...new Set([...blMints, blMintInput.trim()])]; setBlMints(u); setBlMintInput(''); saveToFirestore({ blMints: u });
  };
  const removeBlacklistMint = (mint) => { const u = blMints.filter(m => m !== mint); setBlMints(u); saveToFirestore({ blMints: u }); };

  // quick blacklist from feed (upgrade #12)
  const blacklistMintFromFeed = (mint, label) => {
    if (!mint) return;
    const u = [...new Set([...blMints, mint])]; setBlMints(u); saveToFirestore({ blMints: u });
    showToast(`Blacklisted ${label || shortenAddr(mint)}`, 'info');
  };

  const ignoreTrade = (tradeId) => {
    const u = [...ignoredTrades, tradeId]; setIgnoredTrades(u); saveToFirestore({ ignoredTrades: u });
  };

  const copyAddress = (mint, key) => {
    if (!mint) return; navigator.clipboard.writeText(mint).catch(() => {});
    setCopyFeedback(key); setTimeout(() => setCopyFeedback(null), 1500);
  };

  const openTokenPopup = (trade) => {
    if (!trade.mint) return;
    setTokPopMint(trade.mint); setTokPopLabel(tokenLabel(trade)); setShowTokPop(true); ensureDexData(trade.mint);
  };

  const displayName = (mintOrTrade, fallback) => {
    const mint = typeof mintOrTrade === 'string' ? mintOrTrade : mintOrTrade?.mint;
    if (mint === SOL_MINT) return 'SOL';
    if (mint && dexCache[mint]?.symbol) return dexCache[mint].symbol;
    if (typeof mintOrTrade === 'object' && mintOrTrade?.token) return mintOrTrade.token;
    return fallback || (mint ? shortenAddr(mint) : 'TOKEN');
  };

  const addPriceAlert = () => {
    if (!alertMint.trim()) return;
    const newAlert = { id: Date.now(), mint: alertMint.trim(), targetPrice: alertPrice || null, targetMC: alertMC || null, label: alertLabel || null, createdAt: Date.now() };
    const u = [...priceAlerts, newAlert]; setPriceAlerts(u); saveToFirestore({ priceAlerts: u });
    setAlertMint(''); setAlertPrice(''); setAlertMC(''); setAlertLabel('');
    showToast('Price alert set!', 'success'); ensureDexData(alertMint.trim());
  };
  const removePriceAlert = (id) => { const u = priceAlerts.filter(a => a.id !== id); setPriceAlerts(u); saveToFirestore({ priceAlerts: u }); };

  const openPosition = useCallback(async (trade, amt, source) => {
    const mint = trade.mint || null; if (!mint) return { ok: false, reason: 'no-mint' };
    const info = await fetchPrice(mint); if (!info || !info.price) return { ok: false, reason: 'no-price' };
    const walletInfo = wallets.find(w => w.address === trade.wallet);
    const nm = (mint && dexCache[mint]?.symbol) || trade.token || shortenAddr(mint);
    const rec = {
      token: nm, mint, walletAddr: trade.wallet, wallet: walletInfo?.name || shortenAddr(trade.wallet),
      amt, entryPrice: info.price, currentPrice: info.price, entryMC: info.mc || dexCache[mint]?.fdv || null,
      currentMC: info.mc || null, exitMC: null, exitPrice: null, peakPnlPct: 0, pnlPct: 0, pnlUSD: 0, realizedPnl: 0,
      time: new Date().toLocaleTimeString(), openedAt: Date.now(), open: true, source: source || 'manual',
      rules: { sl: globalSL || null, tp: globalTP || null, trail: globalTrail || null, timeLimit: globalTimeLimit || null, dns: false, parts: [] }
    };
    const newPos = [rec, ...positions]; const newHist = [rec, ...history];
    const newTrading = Math.round((tradingBalance - amt) * 100) / 100;
    setPositions(newPos); setHistory(newHist); setTradingBalance(newTrading);
    setDailyStats(prev => {
      const k = todayKey(); const day = prev[k] || { trades: 0, realized: 0 };
      const updated = { ...prev, [k]: { ...day, trades: day.trades + 1 } };
      saveToFirestore({ positions: newPos, history: newHist, tradingBalance: newTrading, dailyStats: updated }); return updated;
    });
    return { ok: true };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallets, fetchPrice, dexCache, globalSL, globalTP, globalTrail, globalTimeLimit, positions, history, tradingBalance, saveToFirestore]);

  const openCopyModal = (trade) => {
    if (mode === 'live') { setShowRealNotice(true); return; }
    setCopyTrade(trade); setCopyAmt(globalAmt || '50'); setShowCopyModal(true);
  };

  const confirmCopy = async () => {
    const amt = parseFloat(copyAmt) || 0; if (amt <= 0) return;
    if (amt > tradingBalance) {
      showToast('Not enough in Trading Wallet — use Transfer to add funds', 'error');
      setShowCopyModal(false); setShowTransferModal(true); return;
    }
    const res = await openPosition(copyTrade, amt, 'manual');
    if (!res.ok) { if (res.reason === 'no-price') showToast("Can't get live price — try in a few seconds", 'error'); return; }
    showToast(`✓ Copied ${displayName(copyTrade)} — $${amt}`, 'success');
    setShowCopyModal(false); setExpandedTrade(null);
  };

  const closePositionPartial = useCallback((idx, pctOfRemaining, reason, triggeredPartIdx) => {
    setPositions(current => {
      const pos = current[idx]; if (!pos || !pos.open) return current;
      const portion = pctOfRemaining / 100; const portionAmt = pos.amt * portion;
      const portionPnlUSD = (parseFloat(pos.pnlUSD) || 0) * portion;
      const realized = (pos.realizedPnl || 0) + portionPnlUSD; const newAmt = pos.amt - portionAmt;
      const newTrading = Math.round((tradingBalance + portionAmt + portionPnlUSD) * 100) / 100;
      setTradingBalance(newTrading);
      if (newAmt <= 0.01 || pctOfRemaining >= 100) {
        if (realized >= 0) setWins(w => { const nw = w + 1; saveToFirestore({ wins: nw }); return nw; });
        const closedAt = new Date().toLocaleTimeString(); const closedAtMs = Date.now();
        const exitMC = pos.mint && positionMCs[pos.mint] ? positionMCs[pos.mint] : pos.currentMC;
        const exitPrice = pos.currentPrice;
        setHistory(h => { const u = h.map(x => x.openedAt === pos.openedAt ? { ...x, open: false, finalPnl: realized, closedAt, closedAtMs, closeReason: reason || 'manual', exitMC, exitPrice } : x); saveToFirestore({ history: u }); return u; });
        setDailyStats(prev => {
          const k = todayKey(); const day = prev[k] || { trades: 0, realized: 0 };
          const u = { ...prev, [k]: { ...day, realized: Math.round((day.realized + realized) * 100) / 100 } };
          saveToFirestore({ dailyStats: u, tradingBalance: newTrading }); return u;
        });
        if (notifPnl) triggerBlip('summary-pnl', realized >= 0 ? 'up' : 'down');
        showToast(`${reason === 'manual' ? 'Closed' : reason} ${pos.token} · ${realized >= 0 ? '+$' : '-$'}${Math.abs(realized).toFixed(2)}`, realized >= 0 ? 'success' : 'error');
        const newPos = current.map((x, j) => j === idx ? { ...x, open: false, closedAt, realizedPnl: realized, exitMC, exitPrice } : x);
        saveToFirestore({ positions: newPos }); return newPos;
      }
      let updatedParts = pos.rules?.parts || [];
      if (triggeredPartIdx !== undefined) updatedParts = updatedParts.map((pt, pi) => pi === triggeredPartIdx ? { ...pt, triggered: true } : pt);
      setDailyStats(prev => {
        const k = todayKey(); const day = prev[k] || { trades: 0, realized: 0 };
        const u = { ...prev, [k]: { ...day, realized: Math.round((day.realized + portionPnlUSD) * 100) / 100 } };
        saveToFirestore({ dailyStats: u, tradingBalance: newTrading }); return u;
      });
      const newPos = current.map((x, j) => j === idx ? { ...x, amt: newAmt, realizedPnl: realized, rules: { ...x.rules, parts: updatedParts } } : x);
      saveToFirestore({ positions: newPos }); return newPos;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionMCs, notifPnl, triggerBlip, showToast, tradingBalance, saveToFirestore]);

  const manualSell = (idx, pct) => { closePositionPartial(idx, pct, 'manual'); setShowSellModal(false); };

  useEffect(() => {
    positions.forEach((p, idx) => {
      if (!p.open || !p.mint || p.rules?.dns) return;
      const currentPrice = positionPrices[p.mint]; if (!currentPrice || !p.entryPrice) return;
      const pnlPct = ((currentPrice - p.entryPrice) / p.entryPrice) * 100;
      if (p.rules?.sl && pnlPct <= -Math.abs(parseFloat(p.rules.sl))) { closePositionPartial(idx, 100, 'stop-loss'); return; }
      if (p.rules?.tp && pnlPct >= parseFloat(p.rules.tp)) { closePositionPartial(idx, 100, 'take-profit'); return; }
      if (p.rules?.trail) {
        const peak = p.peakPnlPct || 0;
        if (peak > 0 && pnlPct <= peak - Math.abs(parseFloat(p.rules.trail))) { closePositionPartial(idx, 100, 'trailing-stop'); return; }
      }
      if (p.rules?.timeLimit && (Date.now() - p.openedAt) / 3600000 >= parseFloat(p.rules.timeLimit)) { closePositionPartial(idx, 100, 'time-limit'); return; }
      const parts = p.rules?.parts || [];
      for (let pi = 0; pi < parts.length; pi++) {
        if (!parts[pi].triggered && pnlPct >= parseFloat(parts[pi].targetPct)) { closePositionPartial(idx, parseFloat(parts[pi].sellPct), `target-${pi + 1}`, pi); return; }
      }
    });
    setPositions(pos => pos.map(p => {
      if (!p.open || !p.mint) return p;
      const currentPrice = positionPrices[p.mint]; if (!currentPrice || !p.entryPrice) return p;
      const pnlPct = Math.round(((currentPrice - p.entryPrice) / p.entryPrice * 100) * 100) / 100;
      const pnlUSD = Math.round((pnlPct / 100 * p.amt) * 100) / 100;
      const currentMC = positionMCs[p.mint] || p.currentMC;
      const peakPnlPct = Math.max(p.peakPnlPct || 0, pnlPct);
      const prevKey = 'pos-' + p.openedAt; const prev = prevValues.current[prevKey];
      if (prev !== undefined && pnlPct !== prev) triggerBlip(prevKey, pnlPct > prev ? 'up' : 'down');
      prevValues.current[prevKey] = pnlPct;
      return { ...p, currentPrice, pnlPct, pnlUSD, currentMC, peakPnlPct };
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionPrices, positionMCs]);

  useEffect(() => {
    if (!copySell || trades.length === 0) return;
    const latest = trades[0]; if (latest.type !== 'SELL' || !latest.mint) return;
    positions.forEach((p, idx) => {
      if (p.open && p.mint === latest.mint && p.walletAddr === latest.wallet && !p.rules?.dns) closePositionPartial(idx, 100, 'copy-sell');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trades]);

  const logSkip = useCallback((token, wallet, reason) => {
    setSkipLog(prev => {
      const u = [{ token, wallet, reason, time: new Date().toLocaleTimeString(), at: Date.now() }, ...prev].slice(0, 40);
      saveToFirestore({ skipLog: u }); return u;
    });
  }, [saveToFirestore]);

  const markAuto = useCallback((tradeId) => {
    setHistory(h => {
      if (h.some(x => x.autoTradeId === tradeId)) return h;
      const u = [{ autoTradeId: tradeId, open: false, skipped: true, finalPnl: 0, amt: 0, token: '', time: new Date().toLocaleTimeString() }, ...h];
      saveToFirestore({ history: u }); return u;
    });
  }, [saveToFirestore]);

  useEffect(() => {
    if (!autoPilot || trades.length === 0) return;
    const latest = trades[0]; if (!latest.mint || latest.type === 'SELL') return;
    if (history.some(h => h.autoTradeId === latest.id)) return;
    const walletInfo = wallets.find(w => w.address === latest.wallet);
    if (!walletInfo || (walletInfo.mode !== 'copy' && walletInfo.mode !== 'shadow')) return;
    const label = displayName(latest);
    if (blWallets.includes(latest.wallet)) { markAuto(latest.id); logSkip(label, walletInfo.name, 'blacklisted wallet'); return; }
    if (blMints.includes(latest.mint)) { markAuto(latest.id); logSkip(label, walletInfo.name, 'blacklisted token'); return; }
    if (ignoredTrades.includes(latest.id)) return;
    const k = todayKey(); const day = dailyStats[k] || { trades: 0, realized: 0 };
    if (dailyLossLimit && day.realized <= -Math.abs(parseFloat(dailyLossLimit))) { markAuto(latest.id); logSkip(label, walletInfo.name, 'daily loss limit'); return; }
    if (maxDailyTrades && day.trades >= parseInt(maxDailyTrades)) { markAuto(latest.id); logSkip(label, walletInfo.name, 'daily trade limit'); return; }
    if (maxOpenPositions && positions.filter(p => p.open).length >= parseInt(maxOpenPositions)) { markAuto(latest.id); logSkip(label, walletInfo.name, 'max open positions'); return; }
    if (walletCooldown) {
      const cd = parseFloat(walletCooldown) * 60000;
      const lastForWallet = history.find(h => h.walletAddr === latest.wallet && h.source === 'auto');
      if (lastForWallet && (Date.now() - lastForWallet.openedAt) < cd) { markAuto(latest.id); logSkip(label, walletInfo.name, 'wallet cooldown'); return; }
    }
    const dupe = history.find(h => h.walletAddr === latest.wallet && h.mint === latest.mint && (Date.now() - h.openedAt) < 5 * 60000);
    if (dupe) { markAuto(latest.id); logSkip(label, walletInfo.name, 'duplicate buy guard'); return; }
    const walletAmt = parseFloat(walletInfo.amtPerTrade) || parseFloat(globalAmt) || 50;
    const currentExposure = positions.filter(p => p.open && p.walletAddr === latest.wallet).reduce((a, p) => a + p.amt, 0);
    if (walletInfo.maxExposure && currentExposure + walletAmt > parseFloat(walletInfo.maxExposure)) { markAuto(latest.id); logSkip(label, walletInfo.name, 'wallet exposure limit'); return; }
    if (walletAmt > tradingBalance) { markAuto(latest.id); logSkip(label, walletInfo.name, 'insufficient trading balance'); return; }
    (async () => {
      await ensureDexData(latest.mint);
      const dex = dexCache[latest.mint] || await fetchDex(latest.mint);
      if (autoMinMC && dex?.fdv && dex.fdv < parseFloat(autoMinMC)) { markAuto(latest.id); logSkip(label, walletInfo.name, 'below min MC'); return; }
      if (autoMaxMC && dex?.fdv && dex.fdv > parseFloat(autoMaxMC)) { markAuto(latest.id); logSkip(label, walletInfo.name, 'above max MC'); return; }
      if (newTokensOnly) {
        const isNew = dex?.found && dex.ageMs && (Date.now() - dex.ageMs) < 24 * 3600000;
        if (!isNew) { markAuto(latest.id); logSkip(label, walletInfo.name, 'not a new token'); return; }
      }
      const res = await openPosition(latest, walletAmt, 'auto');
      if (res.ok && notifAutoCopy) showToast(`Auto-copied ${displayName(latest)} — $${walletAmt}`, 'success');
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trades]);

  const openRules = (idx) => {
    const pos = positions[idx]; setRulesPosIdx(idx); setRulesSL(pos.rules?.sl || ''); setRulesTP(pos.rules?.tp || '');
    setRulesTrail(pos.rules?.trail || ''); setRulesDNS(pos.rules?.dns || false);
    setRulesParts(pos.rules?.parts?.length > 0 ? pos.rules.parts : [{ targetPct: '', sellPct: '50', triggered: false }]);
    setShowRulesModal(true);
  };
  const addRulesPart = () => { if (rulesParts.length < 3) setRulesParts(p => [...p, { targetPct: '', sellPct: '25', triggered: false }]); };
  const removeRulesPart = (i) => setRulesParts(p => p.filter((_, j) => j !== i));
  const updateRulesPart = (i, field, val) => setRulesParts(p => p.map((pt, j) => j === i ? { ...pt, [field]: val } : pt));
  const saveRules = () => {
    const cleanParts = rulesParts.filter(pt => pt.targetPct !== '' && pt.sellPct !== '');
    const newPos = positions.map((x, j) => j === rulesPosIdx ? { ...x, rules: { ...x.rules, sl: rulesSL || null, tp: rulesTP || null, trail: rulesTrail || null, dns: rulesDNS, parts: cleanParts } } : x);
    setPositions(newPos); saveToFirestore({ positions: newPos }); setShowRulesModal(false);
  };

  const exportCSV = () => {
    const rows = [['Token', 'Wallet', 'Amount', 'Entry MC', 'Exit MC', 'Final P/L', 'Reason', 'Opened', 'Closed']];
    history.filter(h => !h.open && !h.skipped).forEach(h => {
      rows.push([h.token, h.wallet || '', h.amt, h.entryMC || '', h.exitMC || '', h.finalPnl || 0, h.closeReason || '', h.time || '', h.closedAt || '']);
    });
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'mirrortrade_history.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const walletStats = useMemo(() => {
    const stats = {};
    history.forEach(h => {
      if (h.skipped) return; const key = h.walletAddr || h.wallet; if (!key) return;
      if (!stats[key]) stats[key] = { trades: 0, wins: 0, totalPnl: 0, best: null, worst: null, totalHoldMins: 0, closedCount: 0, entryMCsum: 0, entryMCcount: 0, lastTradeAt: 0 };
      if (!h.open) {
        stats[key].trades += 1; const pnl = parseFloat(h.finalPnl) || 0; stats[key].totalPnl += pnl;
        if (pnl >= 0) stats[key].wins += 1;
        if (stats[key].best === null || pnl > stats[key].best) stats[key].best = pnl;
        if (stats[key].worst === null || pnl < stats[key].worst) stats[key].worst = pnl;
        if (h.openedAt && h.closedAtMs) { stats[key].totalHoldMins += (h.closedAtMs - h.openedAt) / 60000; stats[key].closedCount += 1; }
        if (h.entryMC) { stats[key].entryMCsum += h.entryMC; stats[key].entryMCcount += 1; }
        if (h.openedAt > (stats[key].lastTradeAt || 0)) stats[key].lastTradeAt = h.openedAt;
      }
    });
    return stats;
  }, [history]);

  // upgrade #7 - inactive 3d+
  const isWalletInactive = useCallback((addr) => {
    const st = walletStats[addr];
    if (!st || !st.lastTradeAt) {
      const w = wallets.find(x => x.address === addr);
      if (!w?.addedAt) return false;
      return (Date.now() - new Date(w.addedAt).getTime()) > 3 * 86400000;
    }
    return (Date.now() - st.lastTradeAt) > 3 * 86400000;
  }, [walletStats, wallets]);

  const trustScore = useCallback((addr) => {
    const st = walletStats[addr]; if (!st || st.trades === 0) return null;
    const winRate = st.wins / st.trades; const volumeFactor = Math.min(1, st.trades / 20);
    const profitFactor = st.totalPnl > 0 ? 1 : (st.totalPnl === 0 ? 0.5 : 0.2);
    return Math.min(100, Math.round((winRate * 50) + (volumeFactor * 20) + (profitFactor * 30)));
  }, [walletStats]);

  const walletPerformanceBadge = useCallback((addr) => {
    const st = walletStats[addr]; if (!st || st.trades < 3) return null;
    const wr = st.wins / st.trades;
    if (wr >= 0.65) return { label: '\u{1F7E2}', tip: 'Hot wallet' };
    if (wr >= 0.4) return { label: '\u{1F7E1}', tip: 'Average' };
    return { label: '\u{1F534}', tip: 'Cold wallet' };
  }, [walletStats]);

  const consensusMints = useMemo(() => {
    const map = {};
    trades.filter(t => { const ms = t.timestamp?.toMillis ? t.timestamp.toMillis() : 0; return Date.now() - ms < 10 * 60000 && t.type !== 'SELL' && t.mint; })
      .forEach(t => { if (!map[t.mint]) map[t.mint] = new Set(); map[t.mint].add(t.wallet); });
    const out = {}; Object.entries(map).forEach(([mint, set]) => { if (set.size >= 2) out[mint] = set.size; }); return out;
  }, [trades]);

  const reentryTrades = useMemo(() => {
    const out = {};
    trades.forEach(t => {
      if (t.type !== 'BUY' || !t.mint) return;
      const soldBefore = trades.find(x => x.mint === t.mint && x.wallet === t.wallet && x.type === 'SELL' &&
        (x.timestamp?.toMillis ? x.timestamp.toMillis() : 0) < (t.timestamp?.toMillis ? t.timestamp.toMillis() : 0));
      if (soldBefore) out[t.id] = true;
    });
    return out;
  }, [trades]);

  // upgrade #18 - first buyer badge
  const firstBuyerTrades = useMemo(() => {
    const mintFirst = {}; const out = {};
    [...trades].reverse().forEach(t => { if (t.type === 'BUY' && t.mint && !mintFirst[t.mint]) mintFirst[t.mint] = t.id; });
    Object.values(mintFirst).forEach(id => { out[id] = true; }); return out;
  }, [trades]);

  // upgrade #19 - repeat token
  const repeatTokenMints = useMemo(() => {
    const map = {};
    trades.forEach(t => { if (t.type === 'BUY' && t.mint) { if (!map[t.mint]) map[t.mint] = new Set(); map[t.mint].add(t.wallet); } });
    const out = {}; Object.entries(map).forEach(([mint, set]) => { if (set.size >= 2) out[mint] = set.size; }); return out;
  }, [trades]);

  // upgrade #16 - rug risk detector
  const rugRiskMints = useMemo(() => {
    const out = {};
    Object.entries(dexCache).forEach(([mint, dex]) => {
      if (!dex?.found) return;
      const isNew = dex.ageMs && (Date.now() - dex.ageMs) < 30 * 60000;
      const lowLiq = dex.liquidityUsd !== null && dex.liquidityUsd < 10000;
      const highChange = dex.priceChange24h && dex.priceChange24h > 500;
      if (isNew && lowLiq && highChange) out[mint] = true;
    });
    return out;
  }, [dexCache]);

  const heldMints = useMemo(() => { const s = new Set(); positions.forEach(p => { if (p.open && p.mint) s.add(p.mint); }); return s; }, [positions]);
  const sortedWallets = useMemo(() => [...wallets].sort((a, b) => (pinnedWallets.includes(b.address) ? 1 : 0) - (pinnedWallets.includes(a.address) ? 1 : 0)), [wallets, pinnedWallets]);
  const leaderboard = useMemo(() => wallets.map(w => ({ ...w, stats: walletStats[w.address] })).filter(w => w.stats && w.stats.trades > 0).sort((a, b) => (b.stats.wins / b.stats.trades) - (a.stats.wins / a.stats.trades)), [wallets, walletStats]);

  const visibleTrades = useMemo(() => {
    let list = trades.filter(t => !ignoredTrades.includes(t.id) && !blWallets.includes(t.wallet) && !(t.mint && blMints.includes(t.mint)));
    if (selectedWallet) list = list.filter(t => t.wallet === selectedWallet);
    if (feedFilter === 'buy') list = list.filter(t => t.type === 'BUY');
    else if (feedFilter === 'sell') list = list.filter(t => t.type === 'SELL');
    else if (feedFilter === 'new') list = list.filter(t => t.mint && dexCache[t.mint]?.found && dexCache[t.mint]?.ageMs && (Date.now() - dexCache[t.mint].ageMs) < 24 * 3600000);
    return list;
  }, [trades, ignoredTrades, blWallets, blMints, feedFilter, dexCache, selectedWallet]);

  const openPositions = positions.filter(p => p.open);
  const closedHistory = history.filter(h => !h.open && !h.skipped);
  const totalPnl = Math.round(closedHistory.reduce((a, h) => a + (parseFloat(h.finalPnl) || 0), 0) * 100) / 100;
  const winRate = closedHistory.length ? Math.round(wins / closedHistory.length * 100) : 0;
  const todayPnl = (dailyStats[todayKey()]?.realized) || 0;
  const todayTrades = (dailyStats[todayKey()]?.trades) || 0;
  const totalExposure = Math.round(openPositions.reduce((a, p) => a + p.amt, 0) * 100) / 100;
  const unrealizedPnl = Math.round(openPositions.reduce((a, p) => a + (parseFloat(p.pnlUSD) || 0), 0) * 100) / 100;
  const totalWorth = Math.round((reserveBalance + tradingBalance + totalExposure + unrealizedPnl) * 100) / 100;
  const tradingWorth = Math.round((tradingBalance + totalExposure + unrealizedPnl) * 100) / 100;
  const portfolioSOL = solPrice > 0 ? Math.round(totalWorth / solPrice * 1000) / 1000 : 0;
  const tokPopData = tokPopMint ? dexCache[tokPopMint] : null;

  const streak = useMemo(() => {
    let count = 0; let type = null;
    for (const h of closedHistory) {
      const win = (parseFloat(h.finalPnl) || 0) >= 0;
      if (type === null) { type = win; count = 1; } else if (type === win) count += 1; else break;
    }
    return { count, win: type };
  }, [closedHistory]);

  const bestToken = useMemo(() => {
    const map = {}; closedHistory.forEach(h => { if (h.token) map[h.token] = (map[h.token] || 0) + (parseFloat(h.finalPnl) || 0); });
    let best = null; Object.entries(map).forEach(([tok, pnl]) => { if (!best || pnl > best.pnl) best = { tok, pnl }; }); return best;
  }, [closedHistory]);

  useEffect(() => {
    if (prevWorth.current && tradingWorth !== prevWorth.current) triggerBlip('worth', tradingWorth > prevWorth.current ? 'up' : 'down');
    prevWorth.current = tradingWorth;
  }, [tradingWorth, triggerBlip]);

  const chartPath = useMemo(() => {
    if (closedHistory.length < 2) return null;
    let running = 0;
    const points = [...closedHistory].reverse().map(h => { running += (parseFloat(h.finalPnl) || 0); return running; });
    const w = 300, h = 80, pad = 4; const min = Math.min(...points), max = Math.max(...points); const range = (max - min) || 1;
    const d = points.map((v, i) => `${pad + (i / (points.length - 1)) * (w - pad * 2)},${h - pad - ((v - min) / range) * (h - pad * 2)}`).join(' ');
    return { d, color: points[points.length - 1] >= 0 ? '#3fb950' : '#f85149', last: points[points.length - 1], w, h };
  }, [closedHistory]);

  const blipClass = (key) => blips[key] === 'up' ? 'mt-blip-up' : blips[key] === 'down' ? 'mt-blip-down' : '';
  const modeBadge = (m) => m === 'copy' ? 'bg-green-950 text-green-400 border-green-800' : m === 'shadow' ? 'bg-purple-950 text-purple-400 border-purple-800' : 'bg-gray-800 text-gray-400 border-gray-700';
  const modeLabel = (m) => m === 'copy' ? 'COPY' : m === 'shadow' ? 'SHADOW' : 'WATCH';
  const profileWallet = selectedWallet ? wallets.find(w => w.address === selectedWallet) : null;
  const profileStats = selectedWallet ? walletStats[selectedWallet] : null;
  const profileTopTrades = useMemo(() => {
    if (!selectedWallet) return [];
    return closedHistory.filter(h => h.walletAddr === selectedWallet).sort((a, b) => (parseFloat(b.finalPnl) || 0) - (parseFloat(a.finalPnl) || 0)).slice(0, 3);
  }, [selectedWallet, closedHistory]);

  const preTradeChecks = (trade) => {
    const dex = trade.mint ? dexCache[trade.mint] : null;
    const walletInfo = wallets.find(w => w.address === trade.wallet);
    const ts = walletInfo ? trustScore(walletInfo.address) : null;
    return [
      { label: 'Liquidity', ok: dex?.found ? (dex.liquidityUsd === null || dex.liquidityUsd >= 5000) : null, detail: dex?.found ? formatUSD(dex.liquidityUsd) : 'loading' },
      { label: 'Token age', ok: dex?.found ? (dex.ageMs ? (Date.now() - dex.ageMs) > 3600000 : true) : null, detail: dex?.found ? formatAge(dex.ageMs) : 'loading' },
      { label: 'Wallet trust', ok: ts === null ? null : ts >= 50, detail: ts === null ? 'no history' : ts + '/100' },
      { label: 'MC level', ok: dex?.found && dex.fdv ? dex.fdv < 10e6 : null, detail: dex?.found ? formatUSD(dex.fdv) : 'loading' },
    ];
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col">
      <style>{`
        @keyframes mtBlipUp { 0% { background-color: rgba(63,185,80,0.35); } 100% { background-color: transparent; } }
        @keyframes mtBlipDown { 0% { background-color: rgba(248,81,73,0.35); } 100% { background-color: transparent; } }
        .mt-blip-up { animation: mtBlipUp 0.6s ease-out; border-radius: 4px; }
        .mt-blip-down { animation: mtBlipDown 0.6s ease-out; border-radius: 4px; }
        @keyframes slideIn { 0% { transform: translateX(100%); opacity: 0; } 100% { transform: translateX(0); opacity: 1; } }
        .mt-slide-in { animation: slideIn 0.2s ease-out; }
      `}</style>

      {toast && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-[60]">
          <div className={`px-4 py-2 rounded-lg text-xs font-medium shadow-lg border ${toast.kind === 'success' ? 'bg-green-950 border-green-800 text-green-400' : toast.kind === 'error' ? 'bg-red-950 border-red-800 text-red-400' : 'bg-gray-800 border-gray-700 text-white'}`}>{toast.message}</div>
        </div>
      )}

      {/* TOPBAR */}
      <div className="bg-gray-900 border-b border-gray-800 px-4 py-2.5 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <svg width="20" height="20" viewBox="0 0 100 130" fill="none">
            <defs><linearGradient id="mtg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#f2c832"/><stop offset="100%" stopColor="#e8496a"/></linearGradient></defs>
            <path d="M58 4L84 4L84 10L68 10L68 54L84 54L84 62L52 126L42 126L62 72L44 72L44 62L16 62L16 54L32 54L32 10L16 10L16 4L42 4L42 54L58 54Z" fill="url(#mtg)"/>
          </svg>
          <span className="font-bold text-sm bg-gradient-to-r from-yellow-400 to-pink-500 bg-clip-text text-transparent">ArcBolt</span>
          <span className="text-xs px-2 py-0.5 bg-green-950 text-green-400 border border-green-800 rounded ml-1">Mirror</span>
        </div>
        <div className="flex items-center gap-1 overflow-x-auto">
          {['dashboard','wallets','history','settings'].map(t => (
            <button key={t} onClick={() => setActiveTab(t)} className={`text-xs px-3 py-1.5 rounded-lg capitalize transition-all whitespace-nowrap relative ${activeTab === t ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-white'}`}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
              {t === 'dashboard' && newTradeAlert && <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <div className="text-xs text-gray-500">Total worth</div>
            <div className={`text-sm font-bold px-1 ${blipClass('worth')} ${tradingWorth >= 0 ? 'text-green-400' : 'text-red-400'}`}>${Math.round(totalWorth).toLocaleString()}</div>
          </div>
          <div className="flex items-center bg-gray-950 border border-gray-800 rounded-lg p-0.5">
            <button onClick={() => { setMode('live'); saveToFirestore({ mode: 'live' }); }} className={`text-xs px-3 py-1 rounded-md transition-all ${mode === 'live' ? 'bg-gray-800 text-white' : 'text-gray-500'}`}>Live</button>
            <button onClick={() => { setMode('demo'); saveToFirestore({ mode: 'demo' }); }} className={`text-xs px-3 py-1 rounded-md transition-all ${mode === 'demo' ? 'bg-orange-950 text-orange-400' : 'text-gray-500'}`}>Demo</button>
          </div>
          <button onClick={onLogout} className="text-gray-500 hover:text-red-400"><LogOut size={16}/></button>
        </div>
      </div>

      {/* SOL STRIP - upgrade #10 quick stats */}
      <div className="bg-gray-900/50 border-b border-gray-800 px-4 py-1.5 flex items-center gap-3 text-xs overflow-x-auto">
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></div>
          <span className="text-gray-500">SOL</span>
          <span className={`font-bold text-white px-1 ${blipClass('sol')}`}>${solPrice.toFixed(2)}</span>
          <span className={solChange >= 0 ? 'text-green-400' : 'text-red-400'}>{solChange >= 0 ? '+' : ''}{solChange.toFixed(2)}%</span>
        </div>
        <div className="flex items-center gap-3 text-gray-500 flex-shrink-0">
          <span>Today: <span className="text-white">{todayTrades}t</span></span>
          <span>P/L: <span className={todayPnl >= 0 ? 'text-green-400' : 'text-red-400'}>{todayPnl >= 0 ? '+' : ''}${todayPnl.toFixed(0)}</span></span>
          <span>Win: <span className="text-white">{winRate}%</span></span>
        </div>
        <div className="flex items-center gap-3 ml-auto text-gray-500 flex-shrink-0">
          <span className="hidden sm:inline">Reserve: <span className="text-white">${Math.round(reserveBalance).toLocaleString()}</span></span>
          <span>Trading: <span className="text-orange-400">${Math.round(tradingBalance).toLocaleString()}</span></span>
          <span className="hidden sm:inline">At risk: <span className="text-yellow-400">${totalExposure.toFixed(0)}</span></span>
          <span>Unreal: <span className={unrealizedPnl >= 0 ? 'text-green-400' : 'text-red-400'}>{unrealizedPnl >= 0 ? '+' : ''}${unrealizedPnl.toFixed(2)}</span></span>
          {portfolioSOL > 0 && <span className="hidden md:inline text-gray-600">~ <span className="text-blue-400">{portfolioSOL} SOL</span></span>}
          <button onClick={() => setShowTransferModal(true)} className="text-xs px-2 py-0.5 bg-blue-950 border border-blue-800 text-blue-400 rounded">Transfer</button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex">

        {/* ===== DASHBOARD ===== */}
        {activeTab === 'dashboard' && (
          <>
            {/* SIDEBAR */}
            <div className="w-64 border-r border-gray-800 flex flex-col overflow-y-auto flex-shrink-0">
              {!profileWallet ? (
                <div className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Wallets ({wallets.length})</span>
                    <button onClick={pauseAll} className="text-xs text-red-400">Pause all</button>
                  </div>
                  {sortedWallets.length === 0 ? (
                    <div className="text-xs text-gray-600 text-center py-4">No wallets added yet</div>
                  ) : sortedWallets.map((w, i) => {
                    const st = walletStats[w.address];
                    const ts = trustScore(w.address);
                    const inactive = isWalletInactive(w.address);
                    const badge = walletPerformanceBadge(w.address);
                    const exposure = openPositions.filter(p => p.walletAddr === w.address).reduce((a, p) => a + p.amt, 0);
                    return (
                      <div key={i} onClick={() => setSelectedWallet(w.address)} className={`border rounded-lg p-2.5 mb-2 hover:border-gray-700 transition-all cursor-pointer ${inactive ? 'bg-gray-900/40 border-gray-800/50 opacity-60' : 'bg-gray-900 border-gray-800'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          {pinnedWallets.includes(w.address) && <span className="text-yellow-500 text-xs">*</span>}
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: w.color }}></div>
                          <span className="text-xs font-medium text-white truncate flex-1">{w.name}</span>
                          {badge && <span title={badge.tip}>{badge.label}</span>}
                          <button onClick={e => { e.stopPropagation(); cycleWalletMode(w); }} className={`text-xs px-1.5 py-0.5 rounded border ${modeBadge(w.mode)}`}>{modeLabel(w.mode)}</button>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">{shortenAddr(w.address)}</span>
                          {ts !== null && <span className={`text-xs ${ts >= 70 ? 'text-green-400' : ts >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>Trust {ts}</span>}
                        </div>
                        {inactive && <div className="text-xs text-gray-600 mt-1">Inactive 3d+</div>}
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-gray-600">{st && st.trades > 0 ? `${st.trades} trades · ${Math.round(st.wins / st.trades * 100)}%` : 'no trades'}</span>
                          {w.maxExposure && <span className="text-xs text-gray-600">${exposure.toFixed(0)}/${w.maxExposure}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-3">
                  <button onClick={() => setSelectedWallet(null)} className="text-xs text-blue-400 mb-3">Back to all wallets</button>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 rounded-full" style={{ background: profileWallet.color }}></div>
                    <span className="text-sm font-bold text-white flex-1">{profileWallet.name}</span>
                    <button onClick={() => togglePin(profileWallet.address)} className={pinnedWallets.includes(profileWallet.address) ? 'text-yellow-500' : 'text-gray-600'}>*</button>
                  </div>
                  <div className="flex items-center gap-1.5 mb-3 bg-gray-950 rounded px-2 py-1.5">
                    <span className="text-xs text-gray-500 truncate flex-1 font-mono">{shortenAddr(profileWallet.address)}</span>
                    <button onClick={() => copyAddress(profileWallet.address, 'prf')} className="text-xs text-gray-400 hover:text-white">{copyFeedback === 'prf' ? 'copied' : 'copy'}</button>
                  </div>
                  {isWalletInactive(profileWallet.address) && <div className="bg-yellow-950/30 border border-yellow-900 rounded-lg px-3 py-2 text-xs text-yellow-400 mb-3">Inactive for 3+ days</div>}
                  {profileStats && profileStats.trades > 0 ? (
                    <>
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <div className="bg-gray-900 border border-gray-800 rounded-lg p-2"><div className="text-xs text-gray-600">Win rate</div><div className="text-sm font-bold text-green-400">{Math.round(profileStats.wins / profileStats.trades * 100)}%</div></div>
                        <div className="bg-gray-900 border border-gray-800 rounded-lg p-2"><div className="text-xs text-gray-600">Total P/L</div><div className={`text-sm font-bold ${profileStats.totalPnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>{profileStats.totalPnl >= 0 ? '+' : ''}${profileStats.totalPnl.toFixed(0)}</div></div>
                        <div className="bg-gray-900 border border-gray-800 rounded-lg p-2"><div className="text-xs text-gray-600">Trades</div><div className="text-sm font-bold text-white">{profileStats.trades}</div></div>
                        <div className="bg-gray-900 border border-gray-800 rounded-lg p-2"><div className="text-xs text-gray-600">Avg hold</div><div className="text-sm font-bold text-white">{profileStats.closedCount ? (profileStats.totalHoldMins / profileStats.closedCount / 60).toFixed(1) + 'h' : '--'}</div></div>
                      </div>
                      <div className="bg-gray-900 border border-gray-800 rounded-lg p-2 mb-3"><div className="text-xs text-gray-600 mb-0.5">Avg entry MC</div><div className="text-xs font-medium text-white">{profileStats.entryMCcount ? formatUSD(profileStats.entryMCsum / profileStats.entryMCcount) : '--'}</div></div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Top trades</div>
                      {profileTopTrades.map((t, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-800 last:border-0">
                          <span className="text-xs text-white">{t.token}</span>
                          <span className={`text-xs font-medium ${(t.finalPnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{(t.finalPnl || 0) >= 0 ? '+' : ''}${(t.finalPnl || 0).toFixed(2)}</span>
                        </div>
                      ))}
                    </>
                  ) : <div className="text-xs text-gray-600 text-center py-4">No trades from this wallet yet</div>}
                  <a href={`https://solscan.io/account/${profileWallet.address}`} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:text-blue-400 block mt-3">View on Solscan</a>
                </div>
              )}
            </div>

            {/* MAIN AREA */}
            <div className="flex-1 overflow-y-auto p-3">
              {!isDemo && <div className="bg-blue-950/30 border border-blue-900 rounded-lg px-4 py-2 mb-3 text-xs text-gray-400">Live mode - real execution coming soon. <button onClick={() => setMode('demo')} className="text-orange-400">Switch to Demo</button></div>}

              {(streak.count > 1 || bestToken) && (
                <div className="flex items-center gap-3 mb-3 text-xs flex-wrap">
                  {streak.count > 1 && <span className={`px-2 py-1 rounded ${streak.win ? 'bg-green-950 text-green-400' : 'bg-red-950 text-red-400'}`}>{streak.count} {streak.win ? 'wins' : 'losses'} in a row {streak.win ? '🔥' : ''}</span>}
                  {bestToken && <span className="px-2 py-1 rounded bg-gray-800 text-gray-400">Best: {bestToken.tok} {bestToken.pnl >= 0 ? '+' : ''}${bestToken.pnl.toFixed(0)}</span>}
                  <div className="flex items-center gap-2 ml-auto">
                    <span className={`px-2 py-1 rounded ${autoPilot ? 'bg-green-950 text-green-400' : 'bg-gray-800 text-gray-500'}`}>Auto Pilot {autoPilot ? 'ON' : 'OFF'}</span>
                    <button onClick={() => { const na = !autoPilot; setAutoPilot(na); saveToFirestore({ autoPilot: na }); }} className={`w-9 h-4 rounded-full relative transition-all ${autoPilot ? 'bg-green-500' : 'bg-gray-700'}`}><div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${autoPilot ? 'left-5' : 'left-0.5'}`}></div></button>
                  </div>
                </div>
              )}

              {/* FILTER BAR */}
              <div className="flex items-center gap-2 mb-2">
                {selectedWallet && <span className="text-xs text-blue-400">Filtered: {profileWallet?.name} <button onClick={() => setSelectedWallet(null)} className="text-gray-500">x</button></span>}
                <div className="flex gap-1 ml-auto">
                  {['all','buy','sell','new'].map(f => (
                    <button key={f} onClick={() => setFeedFilter(f)} className={`text-xs px-2 py-1 rounded capitalize ${feedFilter === f ? 'bg-gray-800 text-white border border-gray-600' : 'text-gray-500 border border-gray-800'}`}>{f}</button>
                  ))}
                </div>
              </div>

              {/* FEED TABLE */}
              <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden mb-4">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-gray-800 text-xs text-gray-500">
                  <span className="col-span-3">Token</span><span className="col-span-1">Action</span><span className="col-span-2">Wallet</span>
                  <span className="col-span-2">Size</span><span className="col-span-2">MC</span><span className="col-span-2 text-right">Time</span>
                </div>
                {visibleTrades.length === 0 ? <div className="px-3 py-6 text-center text-xs text-gray-600">No trades match this filter</div>
                : visibleTrades.map((trade) => {
                  const walletInfo = wallets.find(w => w.address === trade.wallet);
                  const dex = trade.mint ? dexCache[trade.mint] : null;
                  const isNew = dex?.found && dex.ageMs && (Date.now() - dex.ageMs) < 24 * 3600000;
                  const lowLiq = dex?.found && dex.liquidityUsd !== null && dex.liquidityUsd < 5000;
                  const consensus = trade.mint ? consensusMints[trade.mint] : null;
                  const held = trade.mint && heldMints.has(trade.mint);
                  const expanded = expandedTrade === trade.id;
                  const rugRisk = trade.mint && rugRiskMints[trade.mint];
                  const isFirstBuyer = firstBuyerTrades[trade.id];
                  const isRepeat = trade.mint && repeatTokenMints[trade.mint] >= 2;
                  const badge = walletInfo ? walletPerformanceBadge(walletInfo.address) : null;
                  const isBuy = trade.type === 'BUY'; const isSell = trade.type === 'SELL';
                  return (
                    <div key={trade.id} className={`border-b border-gray-800 last:border-0 ${isBuy ? 'border-l-2 border-l-green-800' : isSell ? 'border-l-2 border-l-red-800' : ''}`}>
                      {/* upgrade #16 - rug risk banner */}
                      {rugRisk && <div className="px-3 py-1 bg-red-950/40 border-b border-red-900 text-xs text-red-400">Rug risk — new token + low liq + 500%+ pump</div>}
                      <div className="grid grid-cols-12 gap-2 px-3 py-2 items-center text-xs">
                        <div className="col-span-3 flex items-center gap-1 min-w-0">
                          {dex?.icon && <img src={dex.icon} alt="" className="w-4 h-4 rounded-full flex-shrink-0" onError={e => { e.target.style.display = 'none'; }}/>}
                          <button onClick={() => openTokenPopup(trade)} className="font-bold text-white hover:text-blue-400 truncate">{displayName(trade)}</button>
                          {isNew && <span className="px-1 rounded bg-purple-950 text-purple-400 flex-shrink-0 text-xs">N</span>}
                          {lowLiq && <span className="px-1 rounded bg-red-950 text-red-400 flex-shrink-0 text-xs" title="Low liq">!</span>}
                          {held && <span className="px-1 rounded bg-blue-950 text-blue-400 flex-shrink-0 text-xs" title="You hold">H</span>}
                          {consensus && <span className="px-1 rounded bg-yellow-950 text-yellow-400 flex-shrink-0">🔥{consensus}</span>}
                          {isFirstBuyer && <span className="flex-shrink-0" title="First buyer">🥇</span>}
                          {isRepeat && <span className="px-1 rounded bg-blue-950 text-blue-400 flex-shrink-0 text-xs" title={`${repeatTokenMints[trade.mint]} wallets`}>👀</span>}
                          {reentryTrades[trade.id] && <span className="flex-shrink-0" title="Re-entry">🔄</span>}
                        </div>
                        <div className="col-span-1"><span className={`px-1.5 py-0.5 rounded font-medium ${typeBadgeClass(trade.type)}`}>{isBuy ? 'BUY' : isSell ? 'SELL' : 'SWAP'}</span></div>
                        <div className="col-span-2 flex items-center gap-1 min-w-0">
                          {walletInfo && <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: walletInfo.color }}></div>}
                          {badge && <span title={badge.tip}>{badge.label}</span>}
                          <button onClick={() => setSelectedWallet(trade.wallet)} className="text-gray-400 hover:text-white truncate">{walletInfo?.name || shortenAddr(trade.wallet)}</button>
                        </div>
                        <div className="col-span-2 text-gray-500">{trade.solSize ? '~' + trade.solSize.toFixed(2) + ' SOL' : '--'}</div>
                        <div className="col-span-2 text-gray-500">{dex?.found && dex.fdv ? formatUSD(dex.fdv) : '--'}</div>
                        <div className="col-span-2 flex items-center justify-end gap-1.5">
                          <span className="text-gray-600">{trade.timestamp?.toDate ? trade.timestamp.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'now'}</span>
                          <button onClick={() => setStatusPanelTrade(trade)} className="text-gray-500 hover:text-white text-xs" title="Info">i</button>
                          {/* upgrade #5 - quick X ignore */}
                          <button onClick={() => ignoreTrade(trade.id)} className="text-gray-600 hover:text-red-400 text-xs" title="Ignore">x</button>
                          <button onClick={() => setExpandedTrade(expanded ? null : trade.id)} className="text-green-500 hover:text-green-400 text-xs">{expanded ? '^' : 'v'}</button>
                        </div>
                      </div>
                      {expanded && (
                        <div className="px-3 py-2 bg-gray-950 border-t border-gray-800">
                          {isBuy ? (
                            <>
                              {/* upgrade #3 - BUY expand: checklist + copy button */}
                              <div className="grid grid-cols-4 gap-2 mb-2">
                                {preTradeChecks(trade).map((c, ci) => (
                                  <div key={ci} className="text-xs">
                                    <div className="flex items-center gap-1">
                                      <span className={c.ok === null ? 'text-gray-500' : c.ok ? 'text-green-400' : 'text-red-400'}>{c.ok === null ? 'o' : c.ok ? 'v' : '!'}</span>
                                      <span className="text-gray-500">{c.label}</span>
                                    </div>
                                    <div className="text-gray-400 ml-4">{c.detail}</div>
                                  </div>
                                ))}
                              </div>
                              {trade.mint && (
                                <div className="flex items-center gap-1.5 mb-2 bg-gray-900 rounded px-2 py-1">
                                  <span className="text-xs text-gray-600">CA</span>
                                  <span className="text-xs text-gray-500 truncate flex-1 font-mono">{trade.mint}</span>
                                  <button onClick={() => copyAddress(trade.mint, 'exp-' + trade.id)} className="text-xs text-gray-400 hover:text-white">{copyFeedback === 'exp-' + trade.id ? 'copied' : 'copy'}</button>
                                </div>
                              )}
                              <div className="flex gap-1.5">
                                <button onClick={() => openCopyModal(trade)} className={`flex-1 text-xs py-1.5 rounded ${isDemo ? 'bg-orange-950 border border-orange-800 text-orange-400' : 'bg-green-950 border border-green-800 text-green-400'}`}>{isDemo ? 'Demo copy' : 'Copy trade'}</button>
                                {trade.mint && <a href={`https://dexscreener.com/solana/${trade.mint}`} target="_blank" rel="noreferrer" className="text-xs px-2 py-1.5 bg-gray-800 border border-gray-700 text-gray-400 rounded">Dex</a>}
                                <a href={`https://solscan.io/tx/${trade.signature}`} target="_blank" rel="noreferrer" className="text-xs px-2 py-1.5 bg-gray-800 border border-gray-700 text-gray-400 rounded">Sol</a>
                                {/* upgrade #12 - blacklist token from feed */}
                                {trade.mint && <button onClick={() => blacklistMintFromFeed(trade.mint, displayName(trade))} className="text-xs px-2 py-1.5 bg-gray-800 border border-gray-700 text-gray-500 rounded" title="Blacklist token">🚫</button>}
                              </div>
                            </>
                          ) : (
                            /* upgrade #4 - SELL expand: exit info, no copy button */
                            (() => {
                              const youHoldThis = trade.mint && heldMints.has(trade.mint);
                              return (
                                <>
                                  {youHoldThis && <div className="bg-yellow-950/30 border border-yellow-900 rounded-lg px-3 py-2 text-xs text-yellow-400 mb-2">You hold this token — wallet is selling!</div>}
                                  <div className="grid grid-cols-3 gap-2 mb-2">
                                    <div className="text-xs bg-gray-900 rounded p-2"><div className="text-gray-600 mb-0.5">Exit MC</div><div className="text-white font-medium">{dex?.found && dex.fdv ? formatUSD(dex.fdv) : '--'}</div></div>
                                    <div className="text-xs bg-gray-900 rounded p-2"><div className="text-gray-600 mb-0.5">Liquidity</div><div className={`font-medium ${dex?.found && dex.liquidityUsd !== null && dex.liquidityUsd < 5000 ? 'text-red-400' : 'text-white'}`}>{dex?.found ? formatUSD(dex.liquidityUsd) : '--'}</div></div>
                                    <div className="text-xs bg-gray-900 rounded p-2"><div className="text-gray-600 mb-0.5">Token age</div><div className="text-white font-medium">{dex?.found ? formatAge(dex.ageMs) : '--'}</div></div>
                                  </div>
                                  {trade.mint && <div className="flex items-center gap-1.5 mb-2 bg-gray-900 rounded px-2 py-1"><span className="text-xs text-gray-600">CA</span><span className="text-xs text-gray-500 truncate flex-1 font-mono">{trade.mint}</span><button onClick={() => copyAddress(trade.mint, 'sexp-' + trade.id)} className="text-xs text-gray-400 hover:text-white">{copyFeedback === 'sexp-' + trade.id ? 'copied' : 'copy'}</button></div>}
                                  <div className="flex gap-1.5">
                                    {trade.mint && <a href={`https://dexscreener.com/solana/${trade.mint}`} target="_blank" rel="noreferrer" className="text-xs px-2 py-1.5 bg-gray-800 border border-gray-700 text-gray-400 rounded">Dex</a>}
                                    <a href={`https://solscan.io/tx/${trade.signature}`} target="_blank" rel="noreferrer" className="text-xs px-2 py-1.5 bg-gray-800 border border-gray-700 text-gray-400 rounded">Sol</a>
                                  </div>
                                </>
                              );
                            })()
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* POSITIONS TABLE */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500 uppercase tracking-wide">Open positions ({openPositions.length})</span>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                {openPositions.length === 0 ? <div className="px-3 py-6 text-center text-xs text-gray-600">No open positions</div> : (
                  <>
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-gray-800 text-xs text-gray-500">
                      <span className="col-span-2">Token</span><span className="col-span-2">Entry MC</span><span className="col-span-2">Amount</span><span className="col-span-2">P/L</span><span className="col-span-1">Age</span><span className="col-span-3 text-right">Actions</span>
                    </div>
                    {positions.map((p, idx) => {
                      if (!p.open) return null;
                      const pnl = parseFloat(p.pnlPct) || 0; const pnlUSD = parseFloat(p.pnlUSD) || 0;
                      const pos = pnl >= 0; const bk = 'pos-' + p.openedAt; const mult = multiplierStr(pnl);
                      const ageHrs = p.openedAt ? (Date.now() - p.openedAt) / 3600000 : 0;
                      const ageWarn = ageHrs >= 24; // upgrade #8
                      return (
                        <div key={idx} className={`grid grid-cols-12 gap-2 px-3 py-2 border-b border-gray-800 last:border-0 items-center text-xs ${pos ? 'border-l-2 border-l-green-800' : 'border-l-2 border-l-red-800'} ${ageWarn ? 'bg-yellow-950/10' : ''}`}>
                          <div className="col-span-2 flex items-center gap-1 min-w-0">
                            <span className="font-bold text-white truncate">{displayName(p.mint, p.token)}</span>
                            {ageWarn && <span className="text-yellow-500 flex-shrink-0" title="Open 24h+">⏰</span>}
                            {p.mint && <button onClick={() => copyAddress(p.mint, 'pca-' + p.openedAt)} className="text-gray-600 hover:text-white flex-shrink-0">{copyFeedback === 'pca-' + p.openedAt ? 'v' : 'c'}</button>}
                          </div>
                          <div className="col-span-2 text-gray-500">{formatUSD(p.entryMC)}</div>
                          <div className="col-span-2 text-gray-400">${p.amt.toFixed(2)}{p.realizedPnl ? <span className="text-gray-600"> (+${p.realizedPnl.toFixed(0)})</span> : null}</div>
                          <div className={`col-span-2 px-1 ${blipClass(bk)}`}>
                            <span className={`font-bold ${pos ? 'text-green-400' : 'text-red-400'}`}>{pos ? '+' : ''}{pnl.toFixed(1)}%</span>
                            {mult && <span className={`ml-1 px-1 rounded ${pos ? 'bg-green-950 text-green-400' : 'bg-red-950 text-red-400'}`}>{mult}</span>}
                            <div className={pos ? 'text-green-400' : 'text-red-400'}>{pos ? '+$' : '-$'}{Math.abs(pnlUSD).toFixed(2)}</div>
                          </div>
                          <div className={`col-span-1 ${ageWarn ? 'text-yellow-500' : 'text-gray-600'}`}>{timeSince(p.openedAt).replace(' ago', '')}</div>
                          <div className="col-span-3 flex items-center justify-end gap-1">
                            <button onClick={() => manualSell(idx, 50)} className="text-xs px-1.5 py-1 bg-red-950/60 border border-red-900 text-red-400 rounded">50%</button>
                            <button onClick={() => manualSell(idx, 100)} className="text-xs px-1.5 py-1 bg-red-950 border border-red-800 text-red-400 rounded">All</button>
                            <button onClick={() => { setSellPosIdx(idx); setSellPct(100); setShowSellModal(true); }} className="text-xs px-1.5 py-1 bg-gray-800 border border-gray-700 text-gray-400 rounded">%</button>
                            <button onClick={() => openRules(idx)} className="text-xs px-1.5 py-1 bg-yellow-950 border border-yellow-800 text-yellow-400 rounded">Rules</button>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          </>
        )}

        {/* ===== WALLETS TAB ===== */}
        {activeTab === 'wallets' && (
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-gray-500 uppercase tracking-wide">Tracked wallets</span>
              <button onClick={pauseAll} className="text-xs px-3 py-1 bg-red-950 border border-red-800 text-red-400 rounded-lg">Pause all</button>
            </div>
            {leaderboard.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 mb-4">
                <div className="text-xs font-medium text-yellow-500 mb-2">Leaderboard (by win rate)</div>
                {leaderboard.slice(0, 5).map((w, i) => (
                  <div key={i} className="flex items-center gap-2 py-1.5 border-b border-gray-800 last:border-0">
                    <span className="text-xs text-gray-600 w-4">#{i + 1}</span>
                    <div className="w-2 h-2 rounded-full" style={{ background: w.color }}></div>
                    <span className="text-xs font-medium text-white flex-1">{w.name}</span>
                    <span className="text-xs text-blue-400">Trust {trustScore(w.address)}</span>
                    <span className="text-xs text-green-400">{Math.round(w.stats.wins / w.stats.trades * 100)}%</span>
                    <span className={`text-xs ${w.stats.totalPnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>{w.stats.totalPnl >= 0 ? '+' : ''}${w.stats.totalPnl.toFixed(0)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              {sortedWallets.map((w, i) => {
                const st = walletStats[w.address]; const ts = trustScore(w.address);
                const inactive = isWalletInactive(w.address); const badge = walletPerformanceBadge(w.address);
                const exposure = openPositions.filter(p => p.walletAddr === w.address).reduce((a, p) => a + p.amt, 0);
                return (
                  <div key={i} className={`bg-gray-900 border rounded-xl p-3 relative ${inactive ? 'border-gray-800/50 opacity-70' : 'border-gray-800'}`}>
                    <div className="absolute top-3 right-3 flex gap-2">
                      <button onClick={() => togglePin(w.address)} className={pinnedWallets.includes(w.address) ? 'text-yellow-500' : 'text-gray-700 hover:text-yellow-500'}>*</button>
                      <button onClick={() => { setEditWallet({ ...w, amtPerTrade: w.amtPerTrade || '', maxExposure: w.maxExposure || '' }); setShowEditWallet(true); }} className="text-gray-700 hover:text-blue-400 text-xs">edit</button>
                      <button onClick={() => { setDelWallet(w); setShowDelModal(true); }} className="text-gray-700 hover:text-red-400">x</button>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full" style={{ background: w.color }}></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1"><span className="text-sm font-medium text-white">{w.name}</span>{badge && <span title={badge.tip}>{badge.label}</span>}</div>
                        <div className="text-xs text-gray-500">{shortenAddr(w.address)}</div>
                      </div>
                      <button onClick={() => cycleWalletMode(w)} className={`mr-20 text-xs px-2 py-0.5 rounded border ${modeBadge(w.mode)}`}>{modeLabel(w.mode)}</button>
                    </div>
                    {inactive && <div className="text-xs text-yellow-600 mb-2">Inactive 3d+</div>}
                    {ts !== null && (
                      <div className="mb-2">
                        <div className="flex items-center justify-between mb-1"><span className="text-xs text-gray-600">Trust score</span><span className={`text-xs font-bold ${ts >= 70 ? 'text-green-400' : ts >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>{ts}/100</span></div>
                        <div className="w-full h-1.5 bg-gray-800 rounded-full"><div className="h-1.5 rounded-full" style={{ width: ts + '%', background: ts >= 70 ? '#3fb950' : ts >= 40 ? '#d29922' : '#f85149' }}></div></div>
                      </div>
                    )}
                    {st && st.trades > 0 ? (
                      <div className="grid grid-cols-4 gap-2 mb-2">
                        <div className="bg-gray-950 rounded p-1.5 text-center"><div className="text-xs font-bold text-green-400">{Math.round(st.wins / st.trades * 100)}%</div><div className="text-xs text-gray-600">Win</div></div>
                        <div className="bg-gray-950 rounded p-1.5 text-center"><div className="text-xs font-bold text-white">{st.trades}</div><div className="text-xs text-gray-600">Trades</div></div>
                        <div className="bg-gray-950 rounded p-1.5 text-center"><div className="text-xs font-bold text-green-400">{st.best !== null ? '+$' + st.best.toFixed(0) : '--'}</div><div className="text-xs text-gray-600">Best</div></div>
                        <div className="bg-gray-950 rounded p-1.5 text-center"><div className="text-xs font-bold text-white">{st.entryMCcount ? formatUSD(st.entryMCsum / st.entryMCcount) : '--'}</div><div className="text-xs text-gray-600">Avg MC</div></div>
                      </div>
                    ) : <div className="text-xs text-gray-600 mb-2">No trades yet</div>}
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span>Amt: <span className="text-white">{w.amtPerTrade ? '$' + w.amtPerTrade : 'global'}</span></span>
                      <span>Max: <span className="text-white">{w.maxExposure ? '$' + w.maxExposure : '--'}</span></span>
                      {w.maxExposure && <span>Used: <span className="text-orange-400">${exposure.toFixed(0)}</span></span>}
                    </div>
                  </div>
                );
              })}
            </div>
            <button onClick={() => setShowAddWallet(true)} className="w-full py-2 border border-dashed border-gray-700 text-gray-500 text-xs rounded-lg hover:border-gray-500 mb-4">+ Add wallet</button>
            {showAddWallet && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-4 max-w-md">
                <div className="text-sm font-medium mb-4">Add Solana wallet</div>
                <label className="text-xs text-gray-500 mb-1 block">Name</label>
                <input value={newWalletName} onChange={e => setNewWalletName(e.target.value)} placeholder="e.g. Whale 1" className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-yellow-500 mb-3"/>
                <label className="text-xs text-gray-500 mb-1 block">Solana wallet address</label>
                <input value={newWalletAddr} onChange={e => setNewWalletAddr(e.target.value)} placeholder="Public address only" className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-yellow-500 mb-3"/>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div><label className="text-xs text-gray-500 mb-1 block">Amount/trade ($)</label><input value={newWalletAmt} onChange={e => setNewWalletAmt(e.target.value)} type="number" placeholder="global" className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none"/></div>
                  <div><label className="text-xs text-gray-500 mb-1 block">Max exposure ($)</label><input value={newWalletMaxExp} onChange={e => setNewWalletMaxExp(e.target.value)} type="number" placeholder="none" className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none"/></div>
                </div>
                <label className="text-xs text-gray-500 mb-2 block">Color</label>
                <div className="flex gap-2 mb-3">{['#3fb950','#58a6ff','#f85149','#d29922','#bc8cff'].map(c => (<div key={c} onClick={() => setNewWalletColor(c)} className="w-6 h-6 rounded-full cursor-pointer" style={{ background: c, border: newWalletColor === c ? '2px solid white' : '2px solid transparent' }}></div>))}</div>
                <label className="text-xs text-gray-500 mb-2 block">Mode</label>
                <div className="flex gap-2 mb-3">{['watch','copy','shadow'].map(m => (<button key={m} onClick={() => setNewWalletMode(m)} className={`flex-1 text-xs py-2 rounded-lg border capitalize ${newWalletMode === m ? modeBadge(m) : 'border-gray-700 text-gray-500'}`}>{m}</button>))}</div>
                <div className="bg-yellow-950/30 border border-yellow-900 rounded-lg px-3 py-2 text-xs text-yellow-500 mb-4">Public address only — never enter private keys</div>
                <div className="flex gap-2"><button onClick={addWallet} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm py-2 rounded-lg">Add</button><button onClick={() => setShowAddWallet(false)} className="flex-1 bg-gray-800 text-gray-400 text-sm py-2 rounded-lg">Cancel</button></div>
              </div>
            )}
          </div>
        )}

        {/* ===== HISTORY TAB ===== */}
        {activeTab === 'history' && (
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-gray-500 uppercase tracking-wide">History</span>
              <button onClick={exportCSV} className="text-xs px-3 py-1 bg-gray-800 border border-gray-700 text-gray-300 rounded-lg">Export CSV</button>
            </div>
            <div className="grid grid-cols-5 gap-2 mb-4">
              {[['All-time P/L', (totalPnl>=0?'+$':'-$')+Math.abs(totalPnl).toFixed(0), totalPnl>=0?'text-green-400':'text-red-400'],['Today P/L',(todayPnl>=0?'+$':'-$')+Math.abs(todayPnl).toFixed(0),todayPnl>=0?'text-green-400':'text-red-400'],['Win rate',closedHistory.length?winRate+'%':'--','text-white'],['Closed',closedHistory.length,'text-white'],['Streak',streak.count>1?`${streak.count} ${streak.win?'W':'L'}`:'--',streak.win?'text-green-400':'text-red-400']].map(([l,v,c],i)=>(
                <div key={i} className="bg-gray-900 border border-gray-800 rounded-lg p-2.5"><div className="text-xs text-gray-500 mb-1">{l}</div><div className={`text-base font-bold ${c}`}>{v}</div></div>
              ))}
            </div>
            {chartPath && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 mb-4">
                <div className="text-xs text-gray-500 mb-2">P/L curve</div>
                <svg viewBox={`0 0 ${chartPath.w} ${chartPath.h}`} className="w-full" style={{ height: 60 }}><polyline points={chartPath.d} fill="none" stroke={chartPath.color} strokeWidth="1.5"/></svg>
                <div className={`text-xs text-center mt-1 font-medium ${chartPath.last>=0?'text-green-400':'text-red-400'}`}>{chartPath.last>=0?'+':''}${chartPath.last.toFixed(2)}</div>
              </div>
            )}
            {closedHistory.length === 0 ? <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-center text-xs text-gray-500 mb-4">No closed trades yet</div> : (
              <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden mb-4">
                <div className="grid grid-cols-8 gap-2 px-4 py-2 border-b border-gray-800 text-xs text-gray-500"><span>Token</span><span>Wallet</span><span>Amt</span><span>Entry MC</span><span>Exit MC</span><span>Held</span><span>P/L</span><span>Reason</span></div>
                {closedHistory.map((r,i) => (
                  <div key={i} className="grid grid-cols-8 gap-2 px-4 py-2 border-b border-gray-800 last:border-0 text-xs items-center">
                    <span className="font-medium text-white truncate">{r.token}</span>
                    <span className="text-gray-500 truncate">{r.wallet}</span>
                    <span className="text-gray-400">${(r.amt||0).toFixed(2)}</span>
                    <span className="text-gray-500">{formatUSD(r.entryMC)}</span>
                    <span className="text-gray-500">{formatUSD(r.exitMC)}</span>
                    <span className="text-gray-600">{holdDuration(r.openedAt,r.closedAtMs)}</span>
                    <span className={(r.finalPnl||0)>=0?'text-green-400':'text-red-400'}>{(r.finalPnl||0)>=0?'+$':'-$'}{Math.abs(r.finalPnl||0).toFixed(2)}</span>
                    <span className="text-gray-600 truncate">{r.closeReason||'manual'}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              {trades.slice(0, 30).map((trade, i) => {
                const walletInfo = wallets.find(w => w.address === trade.wallet);
                return (
                  <div key={i} className="flex items-center gap-3 px-4 py-2 border-b border-gray-800 last:border-0 text-xs">
                    <span className={`px-1.5 py-0.5 rounded font-medium ${typeBadgeClass(trade.type)}`}>{trade.type==='BUY'||trade.type==='SELL'?trade.type:'SWAP'}</span>
                    <span className="font-medium text-white w-24 truncate">{displayName(trade)}</span>
                    <span className="text-gray-500 flex-1 truncate">{walletInfo?.name||shortenAddr(trade.wallet)}</span>
                    {trade.solSize && <span className="text-gray-600">~{trade.solSize.toFixed(1)} SOL</span>}
                    {trade.mint && <a href={`https://dexscreener.com/solana/${trade.mint}`} target="_blank" rel="noreferrer" className="text-blue-500">Dex</a>}
                    <a href={`https://solscan.io/tx/${trade.signature}`} target="_blank" rel="noreferrer" className="text-blue-500">Sol</a>
                    <span className="text-gray-600">{trade.timestamp?.toDate?trade.timestamp.toDate().toLocaleTimeString():'now'}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ===== SETTINGS TAB ===== */}
        {activeTab === 'settings' && (
          <div className="flex-1 overflow-y-auto p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-3">Auto Pilot</div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-800">
                    <div><div className="text-xs">Master switch</div><div className="text-xs text-gray-600">Off = everything manual</div></div>
                    <button onClick={() => { const na=!autoPilot; setAutoPilot(na); saveToFirestore({autoPilot:na}); }} className={`w-8 h-4 rounded-full relative transition-all ${autoPilot?'bg-green-500':'bg-gray-700'}`}><div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${autoPilot?'left-4':'left-0.5'}`}></div></button>
                  </div>
                  {[['Default amount/trade ($)',globalAmt,setGlobalAmt,'globalAmt','50'],['Min market cap ($)',autoMinMC,setAutoMinMC,'autoMinMC','none'],['Max market cap ($)',autoMaxMC,setAutoMaxMC,'autoMaxMC','none']].map(([l,v,s,k,ph])=>(
                    <div key={l} className="flex items-center justify-between py-2 border-b border-gray-800"><span className="text-xs">{l}</span><input value={v} onChange={e=>{s(e.target.value);saveToFirestore({[k]:e.target.value});}} type="number" placeholder={ph} className="bg-gray-950 border border-gray-700 rounded px-2 py-1 text-xs w-24 text-right outline-none"/></div>
                  ))}
                  <div className="flex items-center justify-between py-2 border-b border-gray-800">
                    <div><div className="text-xs">New tokens only</div><div className="text-xs text-gray-600">Under 24h old</div></div>
                    <button onClick={()=>{const nv=!newTokensOnly;setNewTokensOnly(nv);saveToFirestore({newTokensOnly:nv});}} className={`w-8 h-4 rounded-full relative transition-all ${newTokensOnly?'bg-green-500':'bg-gray-700'}`}><div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${newTokensOnly?'left-4':'left-0.5'}`}></div></button>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <div><div className="text-xs">Copy sell</div><div className="text-xs text-gray-600">Auto-close when wallet sells</div></div>
                    <button onClick={()=>{const nv=!copySell;setCopySell(nv);saveToFirestore({copySell:nv});}} className={`w-8 h-4 rounded-full relative transition-all ${copySell?'bg-green-500':'bg-gray-700'}`}><div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${copySell?'left-4':'left-0.5'}`}></div></button>
                  </div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-3">Default Auto-Sell Rules</div>
                  {[['Take profit (%)',globalTP,setGlobalTP,'globalTP','+100'],['Stop loss (%)',globalSL,setGlobalSL,'globalSL','30'],['Trailing stop (%)',globalTrail,setGlobalTrail,'globalTrail','15'],['Time limit (hrs)',globalTimeLimit,setGlobalTimeLimit,'globalTimeLimit','off']].map(([l,v,s,k,ph])=>(
                    <div key={l} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0"><span className="text-xs">{l}</span><input value={v} onChange={e=>{s(e.target.value);saveToFirestore({[k]:e.target.value});}} type="number" placeholder={ph} className="bg-gray-950 border border-gray-700 rounded px-2 py-1 text-xs w-20 text-right outline-none"/></div>
                  ))}
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-3">Safety Limits</div>
                  {[['Daily loss limit ($)',dailyLossLimit,setDailyLossLimit,'dailyLossLimit','off','Auto pilot stops if hit'],['Max trades/day',maxDailyTrades,setMaxDailyTrades,'maxDailyTrades','off','Prevents overtrading'],['Max open positions',maxOpenPositions,setMaxOpenPositions,'maxOpenPositions','off','Total across all wallets'],['Per-wallet cooldown (min)',walletCooldown,setWalletCooldown,'walletCooldown','2','Min gap between auto-trades']].map(([l,v,s,k,ph,d])=>(
                    <div key={l} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0"><div><div className="text-xs">{l}</div><div className="text-xs text-gray-600">{d}</div></div><input value={v} onChange={e=>{s(e.target.value);saveToFirestore({[k]:e.target.value});}} type="number" placeholder={ph} className="bg-gray-950 border border-gray-700 rounded px-2 py-1 text-xs w-20 text-right outline-none flex-shrink-0"/></div>
                  ))}
                </div>
                {/* upgrade #24 - Price / MC alerts */}
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Price / MC Alerts</div>
                  <div className="text-xs text-gray-600 mb-3">Toast when token hits target price or market cap.</div>
                  <input value={alertLabel} onChange={e=>setAlertLabel(e.target.value)} placeholder="Label (e.g. WINS)" className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-xs text-white outline-none mb-2"/>
                  <input value={alertMint} onChange={e=>setAlertMint(e.target.value)} placeholder="Token mint address" className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-xs text-white outline-none mb-2"/>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <input value={alertPrice} onChange={e=>setAlertPrice(e.target.value)} type="number" placeholder="Target price ($)" className="bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-xs text-white outline-none"/>
                    <input value={alertMC} onChange={e=>setAlertMC(e.target.value)} type="number" placeholder="Target MC ($)" className="bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-xs text-white outline-none"/>
                  </div>
                  <button onClick={addPriceAlert} className="w-full text-xs py-2 bg-blue-950 border border-blue-800 text-blue-400 rounded-lg mb-3">Set alert</button>
                  {priceAlerts.map(a => (
                    <div key={a.id} className={`flex items-center gap-2 py-2 border-b border-gray-800 last:border-0 text-xs ${firedAlerts.includes(a.id)?'opacity-50':''}`}>
                      <span className="text-white flex-1">{a.label||shortenAddr(a.mint)}</span>
                      {a.targetPrice && <span className="text-gray-500">&ge;{formatPrice(parseFloat(a.targetPrice))}</span>}
                      {a.targetMC && <span className="text-gray-500">MC&ge;{formatUSD(parseFloat(a.targetMC))}</span>}
                      {firedAlerts.includes(a.id) && <span className="text-green-400">Hit!</span>}
                      <button onClick={()=>removePriceAlert(a.id)} className="text-gray-600 hover:text-red-400">x</button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-3">Account</div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-800"><span className="text-xs">Reserve</span><span className="text-xs text-white">${Math.round(reserveBalance).toLocaleString()}</span></div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-800"><span className="text-xs">Trading wallet</span><span className="text-xs text-orange-400">${Math.round(tradingBalance).toLocaleString()}</span></div>
                  <div className="flex items-center justify-between py-2"><span className="text-xs">Transfer</span><button onClick={()=>setShowTransferModal(true)} className="text-xs px-3 py-1 bg-blue-950 border border-blue-800 text-blue-400 rounded">Transfer</button></div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-3">Notifications</div>
                  {[['New trade detected',notifNewTrade,setNotifNewTrade,'notifNewTrade'],['Auto copy executed',notifAutoCopy,setNotifAutoCopy,'notifAutoCopy'],['P/L alerts',notifPnl,setNotifPnl,'notifPnl'],['Sound alert',notifSound,setNotifSound,'notifSound']].map(([l,v,s,k])=>(
                    <div key={l} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0"><span className="text-xs">{l}</span><button onClick={()=>{const nv=!v;s(nv);saveToFirestore({[k]:nv});}} className={`w-8 h-4 rounded-full relative transition-all ${v?'bg-green-500':'bg-gray-700'}`}><div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${v?'left-4':'left-0.5'}`}></div></button></div>
                  ))}
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-3">Blacklist — wallets</div>
                  <div className="flex gap-2 mb-3"><input value={blWalletInput} onChange={e=>setBlWalletInput(e.target.value)} placeholder="Wallet address" className="flex-1 bg-gray-950 border border-gray-700 rounded px-2 py-1.5 text-xs outline-none"/><button onClick={addBlacklistWallet} className="text-xs px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-gray-300">Add</button></div>
                  <div className="flex flex-wrap gap-2">{blWallets.length===0?<span className="text-xs text-gray-600">None</span>:blWallets.map(addr=>(<span key={addr} className="text-xs px-2 py-1 bg-red-950 border border-red-900 text-red-400 rounded flex items-center gap-1.5">{shortenAddr(addr)}<button onClick={()=>removeBlacklistWallet(addr)}>x</button></span>))}</div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-3">Blacklist — tokens</div>
                  <div className="flex gap-2 mb-3"><input value={blMintInput} onChange={e=>setBlMintInput(e.target.value)} placeholder="Token mint" className="flex-1 bg-gray-950 border border-gray-700 rounded px-2 py-1.5 text-xs outline-none"/><button onClick={addBlacklistMint} className="text-xs px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-gray-300">Add</button></div>
                  <div className="flex flex-wrap gap-2">{blMints.length===0?<span className="text-xs text-gray-600">None</span>:blMints.map(mint=>(<span key={mint} className="text-xs px-2 py-1 bg-red-950 border border-red-900 text-red-400 rounded flex items-center gap-1.5">{shortenAddr(mint)}<button onClick={()=>removeBlacklistMint(mint)}>x</button></span>))}</div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-3">Auto-pilot skip log</div>
                  {skipLog.length===0?<div className="text-xs text-gray-600">No skipped trades</div>:skipLog.slice(0,10).map((s,i)=>(
                    <div key={i} className="flex items-center gap-2 py-1.5 border-b border-gray-800 last:border-0">
                      <span className="text-xs text-gray-400">{s.token||'--'}</span>
                      <span className="text-xs text-gray-600 flex-1 truncate">{s.reason}</span>
                      <span className="text-xs text-gray-700">{s.time}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-3">Data</div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-800"><span className="text-xs">Export history</span><button onClick={exportCSV} className="text-xs px-3 py-1 bg-gray-800 border border-gray-700 text-gray-300 rounded">CSV</button></div>
                  <div className="flex items-center justify-between py-2"><span className="text-xs">Reset account</span><button onClick={()=>{if(window.confirm('Reset all data?')){const p={reserveBalance:100000,tradingBalance:0,positions:[],history:[],wins:0,dailyStats:{},skipLog:[]};setReserveBalance(100000);setTradingBalance(0);setPositions([]);setHistory([]);setWins(0);setDailyStats({});setSkipLog([]);saveToFirestore(p);}}} className="text-xs px-3 py-1 bg-red-950 border border-red-800 text-red-400 rounded">Reset</button></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* STATUS PANEL */}
      {statusPanelTrade && (
        <div className="fixed inset-0 z-[55] flex justify-end" onClick={()=>setStatusPanelTrade(null)}>
          <div className="w-80 bg-gray-900 border-l border-gray-800 h-full overflow-y-auto p-4 mt-slide-in" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-bold">Trade info</span>
              <button onClick={()=>setStatusPanelTrade(null)} className="text-gray-500 hover:text-white">x</button>
            </div>
            {(()=>{
              const t=statusPanelTrade;
              const walletInfo=wallets.find(w=>w.address===t.wallet);
              const dex=t.mint?dexCache[t.mint]:null;
              const soldAfter=trades.find(x=>x.mint===t.mint&&x.wallet===t.wallet&&x.type==='SELL'&&(x.timestamp?.toMillis?x.timestamp.toMillis():0)>(t.timestamp?.toMillis?t.timestamp.toMillis():0));
              const walletTokenTrades=trades.filter(x=>x.mint===t.mint&&x.wallet===t.wallet);
              return (<>
                <div className="flex items-center gap-2 mb-3">
                  {dex?.icon&&<img src={dex.icon} alt="" className="w-6 h-6 rounded-full" onError={e=>{e.target.style.display='none';}}/>}
                  <span className="text-sm font-bold">{displayName(t)}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${typeBadgeClass(t.type)}`}>{t.type==='BUY'||t.type==='SELL'?t.type:'SWAP'}</span>
                </div>
                {walletInfo&&<div className="bg-gray-950 rounded-lg p-3 mb-3"><div className="text-xs text-gray-500 mb-1">Wallet</div><div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full" style={{background:walletInfo.color}}></div><span className="text-xs text-white">{walletInfo.name}</span>{trustScore(walletInfo.address)!==null&&<span className="text-xs text-blue-400 ml-auto">Trust {trustScore(walletInfo.address)}</span>}</div></div>}
                <div className={`rounded-lg p-3 mb-3 border ${soldAfter?'bg-red-950/30 border-red-900':'bg-green-950/30 border-green-900'}`}>
                  <div className="text-xs text-gray-500 mb-1">Wallet's status</div>
                  <div className={`text-sm font-bold ${soldAfter?'text-red-400':'text-green-400'}`}>{soldAfter?`Sold ${timeSince(soldAfter.timestamp?.toMillis?soldAfter.timestamp.toMillis():0)}`:'Still holding'}</div>
                  {soldAfter&&<div className="text-xs text-gray-500 mt-1">Late entry risk — wallet already exited.</div>}
                </div>
                {dex?.found&&<div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-gray-950 rounded-lg p-2"><div className="text-xs text-gray-600">Price</div><div className="text-xs font-medium text-white">{formatPrice(dex.priceUsd)}</div></div>
                  <div className="bg-gray-950 rounded-lg p-2"><div className="text-xs text-gray-600">MC</div><div className="text-xs font-medium text-white">{formatUSD(dex.fdv)}</div></div>
                  <div className="bg-gray-950 rounded-lg p-2"><div className="text-xs text-gray-600">Liquidity</div><div className="text-xs font-medium text-white">{formatUSD(dex.liquidityUsd)}</div></div>
                  <div className="bg-gray-950 rounded-lg p-2"><div className="text-xs text-gray-600">24h</div><div className={`text-xs font-medium ${(dex.priceChange24h||0)>=0?'text-green-400':'text-red-400'}`}>{dex.priceChange24h?(dex.priceChange24h>=0?'+':'')+dex.priceChange24h.toFixed(1)+'%':'--'}</div></div>
                </div>}
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Activity on {displayName(t)}</div>
                {walletTokenTrades.map((x,i)=>(
                  <div key={i} className="flex items-center gap-2 py-1.5 border-b border-gray-800 last:border-0">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${typeBadgeClass(x.type)}`}>{x.type==='BUY'||x.type==='SELL'?x.type:'SWAP'}</span>
                    <span className="text-xs text-gray-500 flex-1">{x.solSize?'~'+x.solSize.toFixed(2)+' SOL':''}</span>
                    <span className="text-xs text-gray-600">{x.timestamp?.toDate?x.timestamp.toDate().toLocaleTimeString():'now'}</span>
                  </div>
                ))}
                <div className="flex gap-2 mt-4">
                  <button onClick={()=>{setStatusPanelTrade(null);openCopyModal(t);}} className={`flex-1 text-xs py-2 rounded ${isDemo?'bg-orange-950 border border-orange-800 text-orange-400':'bg-green-950 border border-green-800 text-green-400'}`}>{isDemo?'Demo copy':'Copy trade'}</button>
                  {t.mint&&<a href={`https://dexscreener.com/solana/${t.mint}`} target="_blank" rel="noreferrer" className="text-xs px-3 py-2 bg-gray-800 border border-gray-700 text-gray-400 rounded">Dex</a>}
                </div>
              </>);
            })()}
          </div>
        </div>
      )}

      {/* COPY MODAL - upgrade #1 $0 guard */}
      {showCopyModal && copyTrade && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-orange-800/50 rounded-2xl p-5 w-full max-w-xs">
            <div className="text-sm font-bold text-orange-400 mb-1">{isDemo?'Demo copy':'Copy trade'}</div>
            <div className="text-xs text-gray-500 mb-3">{displayName(copyTrade)}</div>
            {isDemo&&<div className="bg-orange-950/30 border border-orange-900 rounded-lg px-3 py-2 text-xs text-orange-400 mb-3">Simulated — entry price live from DexScreener</div>}
            {tradingBalance===0&&(
              <div className="bg-red-950/30 border border-red-900 rounded-lg px-3 py-2 text-xs text-red-400 mb-3">
                Trading wallet is $0 — use Transfer button in top strip to add funds from Reserve first
              </div>
            )}
            <div className="text-xs text-gray-500 mb-1">Amount (USD)</div>
            <input value={copyAmt} onChange={e=>setCopyAmt(e.target.value)} type="number" className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-orange-500 mb-2"/>
            <div className="flex gap-2 mb-4">{[10,25,50,100,250].map(v=>(<button key={v} onClick={()=>setCopyAmt(String(v))} className="flex-1 text-xs py-1 bg-gray-800 border border-gray-700 rounded text-gray-400">${v}</button>))}</div>
            <div className="text-xs text-gray-500 mb-4">Trading wallet: <span className={tradingBalance===0?'text-red-400 font-bold':'text-orange-400'}>${Math.round(tradingBalance).toLocaleString()}</span></div>
            <button onClick={confirmCopy} disabled={tradingBalance===0} className={`w-full font-semibold py-2.5 rounded-lg text-sm mb-2 ${tradingBalance===0?'bg-gray-700 text-gray-500 cursor-not-allowed':'bg-orange-500 hover:bg-orange-600 text-white'}`}>{tradingBalance===0?'Add funds first':`Execute ${isDemo?'demo copy':'copy trade'}`}</button>
            <button onClick={()=>setShowCopyModal(false)} className="w-full bg-gray-800 text-gray-400 py-2 rounded-lg text-sm">Cancel</button>
          </div>
        </div>
      )}

      {showRealNotice&&(
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 w-full max-w-xs text-center">
            <div className="text-sm font-bold text-white mb-2">Real trading coming soon</div>
            <div className="text-xs text-gray-500 mb-4">Switch to Demo to practice with the same interface.</div>
            <button onClick={()=>{setShowRealNotice(false);setMode('demo');saveToFirestore({mode:'demo'});}} className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 rounded-lg text-sm mb-2">Switch to Demo</button>
            <button onClick={()=>setShowRealNotice(false)} className="w-full bg-gray-800 text-gray-400 py-2 rounded-lg text-sm">Close</button>
          </div>
        </div>
      )}

      {showTokPop&&(
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={()=>setShowTokPop(false)}>
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 w-full max-w-xs" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              {tokPopData?.icon&&<img src={tokPopData.icon} alt="" className="w-6 h-6 rounded-full" onError={e=>{e.target.style.display='none';}}/>}
              <span className="text-sm font-bold">{(tokPopData&&tokPopData.symbol)||tokPopLabel}</span>
            </div>
            {!tokPopData?<div className="text-xs text-gray-500 text-center py-4">Loading...</div>:tokPopData.found?(
              <>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {[['Price',formatPrice(tokPopData.priceUsd)],['Market cap',formatUSD(tokPopData.fdv)],['Liquidity',formatUSD(tokPopData.liquidityUsd)],['Volume 24h',formatUSD(tokPopData.volume24h)],['24h change',tokPopData.priceChange24h?(tokPopData.priceChange24h>=0?'+':'')+tokPopData.priceChange24h.toFixed(1)+'%':'--'],['Age',formatAge(tokPopData.ageMs)]].map(([l,v])=>(
                    <div key={l} className="bg-gray-950 rounded-lg p-2"><div className="text-xs text-gray-600 mb-0.5">{l}</div><div className="text-xs font-medium text-white">{v}</div></div>
                  ))}
                </div>
                {tokPopData.liquidityUsd!==null&&tokPopData.liquidityUsd<5000&&<div className="bg-red-950/30 border border-red-900 rounded-lg px-3 py-2 text-xs text-red-400 mb-3">Low liquidity</div>}
              </>
            ):<div className="text-xs text-gray-500 mb-3">No pair data on DexScreener.</div>}
            <div className="flex items-center gap-1.5 mb-4 bg-gray-950 rounded px-2 py-1.5">
              <span className="text-xs text-gray-600">CA</span>
              <span className="text-xs text-gray-500 truncate flex-1 font-mono">{tokPopMint}</span>
              <button onClick={()=>copyAddress(tokPopMint,'popup')} className="text-xs text-gray-400 hover:text-white">{copyFeedback==='popup'?'copied':'copy'}</button>
            </div>
            <a href={tokPopData?.url||`https://dexscreener.com/solana/${tokPopMint}`} target="_blank" rel="noreferrer" className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-lg text-sm mb-2">View on DexScreener</a>
            <button onClick={()=>setShowTokPop(false)} className="w-full bg-gray-800 text-gray-400 py-2 rounded-lg text-sm">Close</button>
          </div>
        </div>
      )}

      {showSellModal&&sellPosIdx!==null&&positions[sellPosIdx]&&(
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 w-full max-w-xs">
            <div className="text-sm font-bold text-red-400 mb-1">Sell {positions[sellPosIdx].token}</div>
            <div className="text-xs text-gray-500 mb-4">${positions[sellPosIdx].amt.toFixed(2)} · {(parseFloat(positions[sellPosIdx].pnlPct)||0)>=0?'+':''}{(parseFloat(positions[sellPosIdx].pnlPct)||0).toFixed(2)}%</div>
            <div className="flex gap-2 mb-4">{[25,50,75,100].map(v=>(<button key={v} onClick={()=>setSellPct(v)} className={`flex-1 text-xs py-1.5 rounded border ${sellPct===v?'border-red-600 text-red-400 bg-red-950':'border-gray-700 text-gray-500'}`}>{v}%</button>))}</div>
            <div className="text-xs text-gray-500 mb-4">Selling {sellPct}% = ${(positions[sellPosIdx].amt*sellPct/100).toFixed(2)} + P/L</div>
            <button onClick={()=>manualSell(sellPosIdx,sellPct)} className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 rounded-lg text-sm mb-2">Confirm sell</button>
            <button onClick={()=>setShowSellModal(false)} className="w-full bg-gray-800 text-gray-400 py-2 rounded-lg text-sm">Cancel</button>
          </div>
        </div>
      )}

      {showRulesModal&&rulesPosIdx!==null&&positions[rulesPosIdx]&&(
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 w-full max-w-xs max-h-[85vh] overflow-y-auto">
            <div className="text-sm font-bold mb-1">Rules — {positions[rulesPosIdx].token}</div>
            <div className="text-xs text-gray-500 mb-4">Auto-close when triggered</div>
            <div className={`flex items-center justify-between p-3 rounded-lg border mb-4 ${rulesDNS?'bg-red-950/30 border-red-800':'bg-gray-800 border-gray-700'}`}>
              <div><div className="text-xs font-medium">Do Not Sell</div><div className="text-xs text-gray-500">Master override</div></div>
              <button onClick={()=>setRulesDNS(d=>!d)} className={`w-10 h-5 rounded-full relative transition-all ${rulesDNS?'bg-red-500':'bg-gray-600'}`}><div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${rulesDNS?'left-5':'left-0.5'}`}></div></button>
            </div>
            <div className={rulesDNS?'opacity-30 pointer-events-none':''}>
              {[['Take profit (%)',rulesTP,setRulesTP,'+100'],['Stop loss (%)',rulesSL,setRulesSL,'30'],['Trailing stop (%)',rulesTrail,setRulesTrail,'15']].map(([l,v,s,ph])=>(
                <div key={l} className="flex justify-between items-center mb-3"><span className="text-xs">{l}</span><input value={v} onChange={e=>s(e.target.value)} type="number" placeholder={ph} className="bg-gray-950 border border-gray-700 rounded px-2 py-1 text-xs w-20 text-right outline-none"/></div>
              ))}
              <div className="text-xs text-gray-500 mb-2">DCA-out targets</div>
              {rulesParts.map((pt,i)=>(
                <div key={i} className="flex items-center gap-2 mb-2">
                  <input value={pt.targetPct} onChange={e=>updateRulesPart(i,'targetPct',e.target.value)} type="number" placeholder="+100" className="flex-1 bg-gray-950 border border-gray-700 rounded px-2 py-1.5 text-xs outline-none"/>
                  <span className="text-xs text-gray-600">% sell</span>
                  <input value={pt.sellPct} onChange={e=>updateRulesPart(i,'sellPct',e.target.value)} type="number" placeholder="50" className="w-14 bg-gray-950 border border-gray-700 rounded px-2 py-1.5 text-xs outline-none"/>
                  <span className="text-xs text-gray-600">%</span>
                  <button onClick={()=>removeRulesPart(i)} className="text-xs text-red-400">x</button>
                </div>
              ))}
              {rulesParts.length<3&&<button onClick={addRulesPart} className="text-xs text-blue-400 mb-3">+ Add target</button>}
            </div>
            <button onClick={saveRules} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg text-sm mb-2 mt-2">Save rules</button>
            <button onClick={()=>setShowRulesModal(false)} className="w-full bg-gray-800 text-gray-400 py-2 rounded-lg text-sm">Cancel</button>
          </div>
        </div>
      )}

      {showEditWallet&&editWallet&&(
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 w-full max-w-xs">
            <div className="text-sm font-bold mb-4">Edit {editWallet.name}</div>
            <label className="text-xs text-gray-500 mb-1 block">Name</label>
            <input value={editWallet.name} onChange={e=>setEditWallet(w=>({...w,name:e.target.value}))} className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none mb-3"/>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div><label className="text-xs text-gray-500 mb-1 block">Amt/trade ($)</label><input value={editWallet.amtPerTrade} onChange={e=>setEditWallet(w=>({...w,amtPerTrade:e.target.value}))} type="number" placeholder="global" className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none"/></div>
              <div><label className="text-xs text-gray-500 mb-1 block">Max exposure ($)</label><input value={editWallet.maxExposure} onChange={e=>setEditWallet(w=>({...w,maxExposure:e.target.value}))} type="number" placeholder="none" className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none"/></div>
            </div>
            <label className="text-xs text-gray-500 mb-2 block">Color</label>
            <div className="flex gap-2 mb-3">{['#3fb950','#58a6ff','#f85149','#d29922','#bc8cff'].map(c=>(<div key={c} onClick={()=>setEditWallet(w=>({...w,color:c}))} className="w-6 h-6 rounded-full cursor-pointer" style={{background:c,border:editWallet.color===c?'2px solid white':'2px solid transparent'}}></div>))}</div>
            <label className="text-xs text-gray-500 mb-2 block">Mode</label>
            <div className="flex gap-2 mb-4">{['watch','copy','shadow'].map(m=>(<button key={m} onClick={()=>setEditWallet(w=>({...w,mode:m}))} className={`flex-1 text-xs py-2 rounded-lg border capitalize ${editWallet.mode===m?modeBadge(m):'border-gray-700 text-gray-500'}`}>{m}</button>))}</div>
            <div className="flex gap-2"><button onClick={saveEditWallet} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm py-2 rounded-lg">Save</button><button onClick={()=>{setShowEditWallet(false);setEditWallet(null);}} className="flex-1 bg-gray-800 text-gray-400 text-sm py-2 rounded-lg">Cancel</button></div>
          </div>
        </div>
      )}

      {showTransferModal&&(
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-blue-800/50 rounded-2xl p-5 w-full max-w-xs">
            <div className="text-sm font-bold text-blue-400 mb-3">Transfer funds</div>
            <div className="flex gap-2 mb-4">
              <button onClick={()=>setTransferDir('toTrading')} className={`flex-1 text-xs py-2 rounded-lg border ${transferDir==='toTrading'?'border-orange-600 text-orange-400 bg-orange-950':'border-gray-700 text-gray-500'}`}>Reserve to Trading</button>
              <button onClick={()=>setTransferDir('toReserve')} className={`flex-1 text-xs py-2 rounded-lg border ${transferDir==='toReserve'?'border-gray-500 text-gray-300 bg-gray-800':'border-gray-700 text-gray-500'}`}>Trading to Reserve</button>
            </div>
            <div className="text-xs text-gray-500 mb-2">{transferDir==='toTrading'?`Available: $${Math.round(reserveBalance).toLocaleString()}`:`Free: $${Math.round(tradingBalance).toLocaleString()}`}</div>
            <input value={transferAmt} onChange={e=>setTransferAmt(e.target.value)} type="number" placeholder="Amount" className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-blue-500 mb-2"/>
            <div className="flex gap-2 mb-4">{[1000,5000,10000,25000].map(v=>(<button key={v} onClick={()=>setTransferAmt(String(v))} className="flex-1 text-xs py-1 bg-gray-800 border border-gray-700 rounded text-gray-400">${v/1000}k</button>))}</div>
            <div className="bg-blue-950/30 border border-blue-900 rounded-lg px-3 py-2 text-xs text-blue-400 mb-4">Only Trading Wallet is used for copy trades. Reserve stays safe.</div>
            <button onClick={doTransfer} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg text-sm mb-2">Transfer</button>
            <button onClick={()=>setShowTransferModal(false)} className="w-full bg-gray-800 text-gray-400 py-2 rounded-lg text-sm">Cancel</button>
          </div>
        </div>
      )}

      {showDelModal&&delWallet&&(
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-red-900 rounded-2xl p-5 w-72 text-center">
            <div className="text-sm font-bold text-red-400 mb-2">Remove wallet?</div>
            <div className="text-xs text-gray-500 mb-4">Remove "{delWallet.name}"?</div>
            <div className="flex gap-2"><button onClick={removeWallet} className="flex-1 bg-red-950 border border-red-800 text-red-400 py-2 rounded-lg text-sm">Remove</button><button onClick={()=>setShowDelModal(false)} className="flex-1 bg-gray-800 text-gray-400 py-2 rounded-lg text-sm">Keep</button></div>
          </div>
        </div>
      )}
    </div>
  );

}

export default MT;